import React, { useState, useEffect } from "react";
import * as Styled from "./styled";
import Sidemenu from "../../components/sidemenu";
import { functionService } from "../../lib/functions";
import { Select, DatePicker, Dropdown, Menu, Tooltip, Input, Drawer } from "antd";
import { useNavigate } from "react-router-dom";
import { Button, message } from "antd";
import { CopyOutlined, DownOutlined, FileSearchOutlined, RightOutlined } from "@ant-design/icons";
import brief from "../../images/brief_svg.svg";
import dayjs from "dayjs";
import { Modal } from "antd";


const { RangePicker } = DatePicker;

/* ─────────────────────────────────────────────
   Helper: get the most recently logged interview
   entry (last element pushed into the array)
───────────────────────────────────────────── */

const copyDebriefToClipboard = (debriefText) => {
    if (!debriefText) {
        message.error("No debrief text to copy");
        return;
    }
    navigator.clipboard
        .writeText(debriefText)
        .then(() => {
            message.success("Debrief copied successfully");
        })
        .catch(() => {
            message.error("Failed to copy");
        });
};

const renderDebriefHistoryEmpty = (containerStyle) => (
    <div style={containerStyle}>
        <div
            style={{
                fontSize: "15px",
                fontWeight: 700,
                color: "#111827",
                marginBottom: "14px",
                paddingTop: "2px",
                paddingBottom: "12px",
                borderBottom: "1px solid #E5E7EB",
            }}
        >
            Debrief History
        </div>
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                padding: "28px 16px 24px",
                background: "#F9FAFB",
                border: "1px dashed #E5E7EB",
                borderRadius: "10px",
            }}
        >
            <div
                style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    background: "#EEF2FF",
                    color: "#1B49EA",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "12px",
                }}
            >
                <FileSearchOutlined style={{ fontSize: "18px" }} />
            </div>
            <div
                style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#374151",
                    marginBottom: "4px",
                }}
            >
                No previous debrief
            </div>
            <div
                style={{
                    fontSize: "12px",
                    fontWeight: 400,
                    color: "#9CA3AF",
                    lineHeight: 1.5,
                    maxWidth: "240px",
                }}
            >
                This Role doesn't have any earlier debrief records yet.
            </div>
        </div>
    </div>
);

const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (isNaN(d)) return "-";
    return d.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};
const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (isNaN(d)) return "-";
    return d.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const formatTime = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    if (isNaN(d)) return "-";
    return d.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
    });
};

const paginationStyles = {
    container: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "16px 4px",
        borderTop: "1px solid #F0F0F0",
        marginTop: "8px",
    },
    leftSection: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        fontSize: "13px",
        color: "#6B7280",
    },
    pageControls: { display: "flex", alignItems: "center", gap: "4px" },
    pageBtn: (active) => ({
        minWidth: "34px",
        height: "34px",
        padding: "0 10px",
        borderRadius: "8px",
        border: active ? "none" : "1px solid #E5E7EB",
        background: active ? "#4F46E5" : "#FFFFFF",
        color: active ? "#FFFFFF" : "#374151",
        fontWeight: active ? 600 : 400,
        fontSize: "13px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    }),
    ellipsis: {
        width: "34px",
        height: "34px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#9CA3AF",
        fontSize: "14px",
    },
    navBtn: (disabled) => ({
        height: "34px",
        padding: "0 14px",
        borderRadius: "8px",
        border: "1px solid #E5E7EB",
        background: disabled ? "#F9FAFB" : "#FFFFFF",
        color: disabled ? "#D1D5DB" : "#374151",
        fontWeight: 500,
        fontSize: "13px",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        gap: "4px",
    }),
};

/* ─────────────────────────────────────────────
   Inline styles for the update drawer
───────────────────────────────────────────── */

const drawerStyles = {
    overlay: {
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.30)",
        zIndex: 999,
        transition: "opacity 0.25s ease",
    },
    panel: (open) => ({
        position: "fixed",
        top: 0,
        right: 0,
        height: "100vh",
        width: open ? 400 : 0,
        overflow: "hidden",
        transition: "width 0.28s cubic-bezier(0.4,0,0.2,1)",
        background: "#ffffff",
        borderLeft: "1px solid #e8e8e8",
        boxShadow: open ? "-6px 0 24px rgba(0,0,0,0.10)" : "none",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
    }),
    inner: {
        width: 400,
        padding: "24px 24px 32px",
        boxSizing: "border-box",
        overflowY: "auto",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        gap: 0,
    },
    drawerHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 20,
        paddingBottom: 16,
        borderBottom: "1px solid #f0f0f0",
    },
    drawerTitle: { fontSize: 17, fontWeight: 600, margin: 0, color: "#111" },
    drawerSubtitle: {
        fontSize: 12,
        color: "#999",
        margin: "4px 0 0",
        fontFamily: "monospace",
    },
    closeBtn: {
        width: 30,
        height: 30,
        border: "1px solid #e0e0e0",
        borderRadius: 6,
        background: "transparent",
        cursor: "pointer",
        fontSize: 16,
        color: "#888",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        lineHeight: 1,
    },
    candidateChip: {
        background: "#f7f8fa",
        borderRadius: 10,
        padding: "12px 14px",
        marginBottom: 24,
        display: "flex",
        gap: 12,
        alignItems: "center",
        border: "1px solid #ebebeb",
    },
    avatar: {
        width: 40,
        height: 40,
        borderRadius: "50%",
        background: "#dbeafe",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 13,
        fontWeight: 600,
        color: "#1e40af",
        flexShrink: 0,
    },
    section: { marginBottom: 20 },
    label: {
        fontSize: 12,
        fontWeight: 600,
        color: "#555",
        display: "block",
        marginBottom: 6,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
    },
    hint: { fontSize: 11, color: "#aaa", marginTop: 5 },
    dropzone: (hasFile) => ({
        border: `1.5px dashed ${hasFile ? "#22c55e" : "#d1d5db"}`,
        borderRadius: 10,
        padding: "18px 16px",
        textAlign: "center",
        cursor: "pointer",
        background: hasFile ? "#f0fdf4" : "#fafafa",
        transition: "all 0.2s",
        display: "block",
    }),
    dropzoneText: { fontSize: 13, color: "#666", margin: "6px 0 0" },
    dropzoneSub: { fontSize: 11, color: "#aaa", margin: "3px 0 0" },
    fileSelectedBadge: {
        marginTop: 8,
        padding: "7px 12px",
        background: "#f0fdf4",
        border: "1px solid #bbf7d0",
        borderRadius: 7,
        fontSize: 12,
        color: "#16a34a",
        display: "flex",
        alignItems: "center",
        gap: 6,
    },
    divider: { borderTop: "1px solid #f0f0f0", margin: "20px 0" },
    actionRow: { display: "flex", gap: 10, marginTop: "auto", paddingTop: 16 },
    successBanner: {
        marginTop: 14,
        padding: "10px 14px",
        background: "#f0fdf4",
        border: "1px solid #bbf7d0",
        borderRadius: 8,
        fontSize: 13,
        color: "#16a34a",
        textAlign: "center",
        fontWeight: 500,
    },
    editIconBtn: {
        width: 32,
        height: 32,
        borderRadius: 6,
        background: "#DEE2EF",
        cursor: "pointer",
        fontSize: 15,
        color: "#2563eb",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background 0.15s",
        border: "none",
    },
};

const InterviewTable = () => {
    const [jobListings, setJobListings] = useState([]);
    const [filteredJobListings, setFilteredJobListings] = useState([]);

    const [recruiterOptions, setRecruiterOptions] = useState([]);
    const [selectedRecruiter, setSelectedRecruiter] = useState(null);
    const [companyList, setCompanyList] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);
    const [roleOptions, setRoleOptions] = useState([]);
    const [subAccountOptions, setSubAccountOptions] = useState([]);
    const [selectedSubAccount, setSelectedSubAccount] = useState(null);
    const [endClientOptions, setEndClientOptions] = useState([]);
    const [selectedEndClient, setSelectedEndClient] = useState(null);

    const [interviewDateRange, setInterviewDateRange] = useState(null);


    const [selectedDebriefStatus, setSelectedDebriefStatus] = useState(null);
    const [selectedDebriefDate, setSelectedDebriefDate] = useState(null);

    const [todayCurrentPage, setTodayCurrentPage] = useState(1);
    const [todayItemsPerPage, setTodayItemsPerPage] = useState(10);

    const [allCurrentPage, setAllCurrentPage] = useState(1);
    const [allItemsPerPage, setAllItemsPerPage] = useState(10);

    const [searchText, setSearchText] = useState("");

    const [showInterviewHistory, setShowInterviewHistory] = useState(false);
    const [previousCurrentPage, setPreviousCurrentPage] = useState(1);
    const [previousItemsPerPage, setPreviousItemsPerPage] = useState(10);

    const [selectedRound, setSelectedRound] = useState(null);
    const [selectedCandidateStatus, setSelectedCandidateStatus] = useState(null);

    const [isInterviewDrawerVisible, setIsInterviewDrawerVisible] = useState(false);
    const [selectedInterview, setSelectedInterview] = useState(null);
    const [rescheduleType, setRescheduleType] = useState(null);
    const [newInterviewDate, setNewInterviewDate] = useState(null);
    const [newInterviewDateError, setNewInterviewDateError] = useState("");

    const [isEditInterviewDateTimeVisible, setIsEditInterviewDateTimeVisible] =
        useState(false);

    const [selectedEditInterview, setSelectedEditInterview] =
        useState(null);

    const [editInterviewDateTime, setEditInterviewDateTime] =
        useState(null);

    const [editInterviewDateTimeError, setEditInterviewDateTimeError] =
        useState("");

    const [selectedInterviewDate, setSelectedInterviewDate] = useState(null);
    const [debriefHistoryByCandidate, setDebriefHistoryByCandidate] = useState({});

    const [
        isInterviewNotConductedModalVisible,
        setIsInterviewNotConductedModalVisible
    ] = useState(false);

    const [
        selectedInterviewConducted,
        setSelectedInterviewConducted
    ] = useState(null);

    const [
        notConductedType,
        setNotConductedType
    ] = useState(null);


    const [updateCandidateStatus, setUpdateCandidateStatus] = useState(null);

    const [
        updateCandidateStatusChangedDate,
        setUpdateCandidateStatusChangedDate
    ] = useState(null);

    const [interviewDateTime, setInterviewDateTime] = useState(null);

    const [client_feedback, setClientFeedback] = useState("");

    const [updateClientRemark, setUpdateClientRemark] = useState("");
    const [reason, setReason] = useState("");

    const [isInterviewConductedDrawerVisible, setIsInterviewConductedDrawerVisible] = useState(false);

    // const [selectedInterviewConducted, setSelectedInterviewConducted] = useState(null);

    const [interviewConducted, setInterviewConducted] = useState(null);

    // const [notConductedType, setNotConductedType] = useState(null);

    // 1) Add this state inside InterviewTable (near other useState declarations)
    const [expandedPreviousRows, setExpandedPreviousRows] = useState({});
    const [hoveredPreviousParentKey, setHoveredPreviousParentKey] = useState(null);

    const [isProfileDrawerVisible, setIsProfileDrawerVisible] = useState(false);

    const [selectedProfileCandidate, setSelectedProfileCandidate] = useState(null);

    const [selectedRows, setSelectedRows] = useState([]);
    const navigate = useNavigate();

    const getCompanyName = async () => {
        const response = await functionService.get("demand/demand-companies-list");
        if (response?.response?.data?.data?.length > 0) {
            setCompanyList(response.response.data.data);
        }
    };


    const recruiterSearch = async () => {
        try {
            let res = await functionService.get(`demand/recruiters-list`);
            let apiData = res?.response?.data?.data;
            const optionsWithNames = apiData.map((item) => ({
                ...item,
                recruiter_name: item.label,
            }));
            setRecruiterOptions(optionsWithNames);
        } catch (error) {
            console.error("Error fetching recruiters list:", error);
        }
    };

    // const interviewStatuses = [
    //     "Interview Scheduled (R1)",
    //     "Interview Scheduled (R2)",
    //     "Interview Scheduled (R3)",
    //     "Interview Scheduled (R4)",
    //     "Yet to be Interviewed (R1)",
    //     "Yet to be Interviewed (R2)",
    //     "Yet to be Interviewed (R3)",
    //     "Yet to be Interviewed (R4)"
    // ];


    const isInterviewScheduledStatus = (status) => {
        const value = String(status ?? "").trim();

        return (
            value === "Discussion Round" ||
            /^Interview Scheduled \(R[1-4]\)$/i.test(value)
        );
    };

    const showInterviewDateTime =
        isInterviewScheduledStatus(updateCandidateStatus) &&
        updateCandidateStatus !== selectedProfileCandidate?.candidate_current_status;

    const resolveStatusChangedDate = (status, statusHistory, fallbackDate = null) => {
        return getStatusChangedDateFromHistory(status, statusHistory)
            || (fallbackDate ? dayjs(fallbackDate) : null)
            || (status ? dayjs() : null);
    };

    const MIN_DATE_DEBRIEF = dayjs("2026-06-28").startOf("day");
    const disableDebriefDates = (current) =>
        current && (current > dayjs().endOf("day") || current < MIN_DATE_DEBRIEF);

    /* ── Fetch: only candidates that actually have scheduled_datetime entries ── */
    const fetchInterviewListings = async () => {
        try {
            const res = await functionService.getInterviewListings();

            if (res.status) {
                const jsonData = res.response.data.data;
                console.table(
                    jsonData
                        .filter(
                            (item) =>
                                item.candidate_id === "ea260744-4c08-47e7-8a91-fb2e29d748b8",
                        )
                        .map((item) => ({
                            round: item.round_name,
                            candidate_status: item.candidate_status,
                            interview_status: item.interview_status,
                            interview_schedule: item.scheduled_datetime,
                            remarks: item.remarks,
                        })),
                );

                // Keep only rows having scheduled interview
                const withInterviews = jsonData.filter(
                    (item) => item.scheduled_datetime,
                );

                // Sort by interview date (earliest first)
                const sorted = [...withInterviews].sort(
                    (a, b) =>
                        new Date(a.scheduled_datetime) - new Date(b.scheduled_datetime),
                );

                setJobListings(sorted);
            } else {
                console.error("Error fetching interview listings:", res.error);
            }
        } catch (error) {
            console.error("Error in fetchInterviewListings:", error);
        }
    };

    const handleUpdateProfileStatus = async () => {

        const hasStatus = Boolean(updateCandidateStatus);
        const hasStatusChangedDate = Boolean(updateCandidateStatusChangedDate);

        if (hasStatus !== hasStatusChangedDate) {
            message.error(
                "Candidate Current Status and Status Changed Date must both be filled together."
            );
            return;
        }

        if (showInterviewDateTime && !interviewDateTime) {
            message.error(
                "Please select Interview Date & Time."
            );
            return;
        }

        try {

            const payload = {
                profile_sent: true,

                profile_sent_date:
                    selectedProfileCandidate?.profile_sent_date,

                candidate_current_status:
                    updateCandidateStatus,

                candidate_current_status_time:
                    updateCandidateStatusChangedDate
                        ? updateCandidateStatusChangedDate.toISOString()
                        : null,

                client_feedback,

                client_remark:
                    updateClientRemark,

                interview_datetime:
                    interviewDateTime
                        ? interviewDateTime.toISOString()
                        : null
            };

            await functionService.post(
                `demand/update-profile-sent-status/${selectedProfileCandidate.candidate_id}`,
                payload
            );

            message.success(
                "Profile status updated successfully"
            );

            closeProfileStatusDrawer();

            fetchInterviewListings();

        } catch (error) {

            console.log(error);

            message.error(
                "Unable to update profile status"
            );
        }
    };

    useEffect(() => {
        getCompanyName();
        recruiterSearch();
        fetchInterviewListings();
    }, []);

    useEffect(() => {
        const base = selectedCompany
            ? jobListings.filter((item) => item.company_name === selectedCompany)
            : jobListings;

        setSubAccountOptions(
            [...new Set(base.map((item) => item.sub_account).filter(Boolean))].sort(),
        );
        setEndClientOptions(
            [...new Set(base.map((item) => item.end_client).filter(Boolean))].sort(),
        );
        setRoleOptions(
            [...new Set(base.map((item) => item.role_title).filter(Boolean))].sort(),
        );
    }, [selectedCompany, jobListings]);



    useEffect(() => {
        setSelectedSubAccount(null);
        setSelectedEndClient(null);
        setSelectedRole(null);
    }, [selectedCompany]);

    const fullCandidateStatusOptions = [
        "Yet to be shortlisted",
        "Discussion Round",
        "Pre-Interview Back Out",

        // R1
        "Screen Select - Yet to be Interviewed (R1)",
        "Screen Reject",
        "Interview Scheduled (R1)",
        "During Interview Back Out (R1)",
        "Interviewed - Feedback Pending (R1)",
        "Interviewed - Rejected (R1)",

        // R2
        "Yet to be Interviewed (R2)",
        "Interview Scheduled (R2)",
        "During Interview Back Out (R2)",
        "Interviewed - Feedback Pending (R2)",
        "Interviewed - Rejected (R2)",

        // R3
        "Yet to be Interviewed (R3)",
        "Interview Scheduled (R3)",
        "During Interview Back Out (R3)",
        "Interviewed - Feedback Pending (R3)",
        "Interviewed - Rejected (R3)",

        // R4
        "Yet to be Interviewed (R4)",
        "Interview Scheduled (R4)",
        "During Interview Back Out (R4)",
        "Interviewed - Feedback Pending (R4)",
        "Interviewed - Rejected (R4)",
        "Post Interview Back Out",

        // Final Stage
        "Final Select",
        "Final Reject",
        "Pre Offer Back Out",

        // Offer Stage
        "Offer yet to be released",
        "Offer released",
        "Post Offer Back Out",

        // Joining Stage
        "Offer Accepted - Yet to join",
        "Offer Accepted - Joined",
        "Offer Accepted - Not Joined",
        "Offer Rejected - Not joined",

        // Other Outcomes
        "Position on hold/closed",
        "Change in requirement from client",
        "Duplicate Profile",
        "Profile Reshared"
    ];
    const interviewStatuses = [
        "Interview Scheduled (R1)",
        "Interview Scheduled (R2)",
        "Interview Scheduled (R3)",
        "Interview Scheduled (R4)",
    ];

    const statusFlow = {
        SCREENING: [
            "Yet to be shortlisted",
            "Discussion Round",
            "Pre-Interview Back Out",
            "Screen Select - Yet to be Interviewed (R1)",
            "Interview Scheduled (R1)"
        ],

        R1: [
            "Screen Reject",
            "Interview Scheduled (R1)",
            "During Interview Back Out (R1)",
            "Interviewed - Feedback Pending (R1)",
            "Interviewed - Rejected (R1)",
            "Yet to be Interviewed (R2)",
            "Interview Scheduled (R2)"
        ],

        R2: [
            "Interview Scheduled (R2)",
            "During Interview Back Out (R2)",
            "Interviewed - Feedback Pending (R2)",
            "Interviewed - Rejected (R2)",
            "Yet to be Interviewed (R3)",
            "Interview Scheduled (R3)"
        ],

        R3: [
            "Interview Scheduled (R3)",
            "During Interview Back Out (R3)",
            "Interviewed - Feedback Pending (R3)",
            "Interviewed - Rejected (R3)",
            "Yet to be Interviewed (R4)",
            "Interview Scheduled (R4)"
        ],

        R4: [
            "Interview Scheduled (R4)",
            "During Interview Back Out (R4)",
            "Interviewed - Feedback Pending (R4)",
            "Interviewed - Rejected (R4)"
        ],

        DR: [
            "Discussion Round",
            "Screen Select - Yet to be Interviewed (R1)",
            "Interview Scheduled (R1)"
        ],

        // These are available from every stage
        ALWAYS_AVAILABLE: [
            "Yet to be shortlisted",
            "Discussion Round",
            "Pre-Interview Back Out",

            // R1
            "Screen Select - Yet to be Interviewed (R1)",
            "Screen Reject",
            "Interview Scheduled (R1)",

            // Final Stage
            "Post Interview Back Out",
            "Final Select",
            "Final Reject",
            "Pre Offer Back Out",

            // Offer Stage
            "Offer yet to be released",
            "Offer released",
            "Post Offer Back Out",

            // Joining Stage
            "Offer Accepted - Yet to join",
            "Offer Accepted - Joined",
            "Offer Accepted - Not Joined",
            "Offer Rejected - Not joined",

            // Other Outcomes
            "Position on hold/closed",
            "Change in requirement from client",
            "Duplicate Profile",
            "Profile Reshared"
        ]
    };

    const globalAvailableStatuses = [
        "Yet to be shortlisted",
        "Discussion Round",
        "Pre-Interview Back Out",

        // R1
        "Screen Select - Yet to be Interviewed (R1)",
        "Screen Reject",
        "Interview Scheduled (R1)",

        // Final Stage
        "Post Interview Back Out",
        "Final Select",
        "Final Reject",
        "Pre Offer Back Out",

        // Offer Stage
        "Offer yet to be released",
        "Offer released",
        "Post Offer Back Out",

        // Joining Stage
        "Offer Accepted - Yet to join",
        "Offer Accepted - Joined",
        "Offer Accepted - Not Joined",
        "Offer Rejected - Not joined",

        // Other Outcomes
        "Position on hold/closed",
        "Change in requirement from client",
        "Duplicate Profile",
        "Profile Reshared"
    ];


    const getStatusStage = (status) => {
        if (!status) return "SCREENING";
        if (status === "Discussion Round") return "DR";
        // Yet to be interviewed belongs to previous round
        if (status === "Yet to be Interviewed (R2)") return "R1";
        if (status === "Yet to be Interviewed (R3)") return "R2";
        if (status === "Yet to be Interviewed (R4)") return "R3";

        // Normal round statuses
        if (status.includes("(R1)")) return "R1";
        if (status.includes("(R2)")) return "R2";
        if (status.includes("(R3)")) return "R3";
        if (status.includes("(R4)")) return "R4";

        // Final statuses belong to R4
        if (
            [
                "Post Interview Back Out",
                "Final Select",
                "Final Reject"
            ].includes(status)
        ) {
            return "R4";
        }

        return "SCREENING";
    };


    const currentStatus =
        updateCandidateStatus ||
        selectedProfileCandidate?.candidate_current_status;

    const currentStage = getStatusStage(currentStatus);

    const allowedStatuses = [
        ...(statusFlow[currentStage] || []),
        ...statusFlow.ALWAYS_AVAILABLE
    ];

    const currentStatusIndex =
        fullCandidateStatusOptions.indexOf(currentStatus);

    const isCurrentStatusGlobal =
        statusFlow.ALWAYS_AVAILABLE.includes(currentStatus);

    const candidateStatusOptions = [
        ...fullCandidateStatusOptions
    ];
    useEffect(() => {
        let filtered = jobListings;

        if (selectedCompany)
            filtered = filtered.filter(
                (item) => item.company_name === selectedCompany,
            );
        if (selectedSubAccount)
            filtered = filtered.filter(
                (item) => item.sub_account === selectedSubAccount,
            );
        if (selectedEndClient)
            filtered = filtered.filter(
                (item) => item.end_client === selectedEndClient,
            );
        if (selectedRole)
            filtered = filtered.filter((item) => item.role_title === selectedRole);
        if (selectedRecruiter)
            filtered = filtered.filter(
                (item) => item.recruiter_name === selectedRecruiter,
            );

        if (selectedDebriefStatus) {
            filtered = filtered.filter((item) => {
                let debrief = item.debrief_form;

                if (typeof debrief === "string") {
                    try {
                        debrief = JSON.parse(debrief);
                    } catch {
                        debrief = [];
                    }
                }

                const hasDebrief = Array.isArray(debrief) && debrief.length > 0;
                return selectedDebriefStatus === "Filled" ? hasDebrief : !hasDebrief;
            });
        }

        if (selectedDebriefDate) {
            filtered = filtered.filter((item) => {
                let debrief = item.debrief_form;

                if (typeof debrief === "string") {
                    try {
                        debrief = JSON.parse(debrief);
                    } catch {
                        debrief = [];
                    }
                }

                if (!Array.isArray(debrief) || debrief.length === 0) {
                    return false;
                }

                return debrief.some(
                    (round) =>
                        round.submitted_at &&
                        dayjs(round.submitted_at).isSame(selectedDebriefDate, "day"),
                );
            });
        }

        if (searchText) {
            const search = searchText.toLowerCase();
            filtered = filtered.filter(
                (item) =>
                    item.email?.toLowerCase().includes(search) ||
                    `${item.first_name || ""} ${item.last_name || ""}`
                        .toLowerCase()
                        .includes(search),
            );
        }

        if (selectedRound) {
            filtered = filtered.filter((item) => item.round_name === selectedRound);
        }

        if (selectedCandidateStatus) {
            filtered = filtered.filter(
                (item) => item.candidate_status === selectedCandidateStatus,
            );
        }
        if (interviewDateRange && interviewDateRange.length === 2) {
            const [fromDate, toDate] = interviewDateRange;
            const from = fromDate.startOf("day").valueOf();
            const to = toDate.endOf("day").valueOf();

            filtered = filtered.filter((item) => {
                if (!item.scheduled_datetime) return false;
                const itemDate = new Date(item.scheduled_datetime).getTime();
                if (isNaN(itemDate)) return false;
                return itemDate >= from && itemDate <= to;
            });
        }

        setFilteredJobListings(filtered);

        // reset both section paginations on any filter change
        setTodayCurrentPage(1);
        setAllCurrentPage(1);
    }, [
        selectedCompany,
        selectedSubAccount,
        selectedEndClient,
        selectedRole,
        selectedRecruiter,
        searchText,
        selectedRound,
        selectedCandidateStatus,
        interviewDateRange,
        jobListings,
        selectedDebriefStatus,
        selectedDebriefDate,
    ]);

    const todayDateStr = new Date().toDateString();

    const todayInterviewListings = filteredJobListings.filter((item) => {
        if (!item.scheduled_datetime) return false;

        return new Date(item.scheduled_datetime).toDateString() === todayDateStr;
    });

    const allInterviewListings = filteredJobListings.filter((item) => {
        if (!item.scheduled_datetime) return false;

        const interviewDate = new Date(item.scheduled_datetime);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        interviewDate.setHours(0, 0, 0, 0);

        return interviewDate > today;
    });

    // const previousInterviewListings = filteredJobListings
    //     .filter((item) => {
    //         if (!item.scheduled_datetime) return false;

    //         const interviewDate = new Date(item.scheduled_datetime);

    //         const today = new Date();
    //         today.setHours(0, 0, 0, 0);

    //         interviewDate.setHours(0, 0, 0, 0);

    //         return interviewDate < today;
    //     })
    //     .sort((a, b) => {
    //         return new Date(b.scheduled_datetime) - new Date(a.scheduled_datetime);
    //     });

    // 3) Replace previousInterviewListings with grouped data source
    const previousInterviewListings = filteredJobListings
        .filter((item) => {
            if (!item.scheduled_datetime) return false;

            const interviewDate = new Date(item.scheduled_datetime);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            interviewDate.setHours(0, 0, 0, 0);

            return interviewDate < today;
        })
        .sort(
            (a, b) => new Date(b.scheduled_datetime) - new Date(a.scheduled_datetime),
        );

    // 2) Add these helpers inside InterviewTable (before return)
    const getRoundNumber = (roundName = "") => {
        const match =
            String(roundName).match(/r(\d+)/i) || String(roundName).match(/(\d+)/);
        return match ? parseInt(match[1], 10) : -1;
    };

    const getTieBreakerDate = (item) => {
        const d = item?.updated_at || item?.recorded_at || item?.scheduled_datetime;
        const t = d ? new Date(d).getTime() : 0;
        return Number.isNaN(t) ? 0 : t;
    };

    const getIsInterviewConducted = (item) => {
        const latestInterviewConducted =
            item?.interview_conducted?.[item.interview_conducted.length - 1];
        return latestInterviewConducted?.interview_conducted === true;
    };

    const groupPreviousInterviewsByCandidate = (rows = []) => {
        const grouped = rows.reduce((acc, row) => {
            const key =
                row.candidate_id ||
                `${row.email || ""}-${row.first_name || ""}-${row.last_name || ""}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(row);
            return acc;
        }, {});

        const candidates = Object.entries(grouped).map(
            ([candidateKey, candidateRows]) => {
                const sortedRounds = [...candidateRows].sort((a, b) => {
                    const roundDiff =
                        getRoundNumber(b.round_name) - getRoundNumber(a.round_name);
                    if (roundDiff !== 0) return roundDiff;
                    return getTieBreakerDate(b) - getTieBreakerDate(a);
                });

                return {
                    candidateKey,
                    latest: sortedRounds[0],
                    children: sortedRounds.slice(1),
                    all: sortedRounds,
                };
            },
        );

        return candidates.sort((a, b) => {
            const aDate = a.latest?.scheduled_datetime
                ? new Date(a.latest.scheduled_datetime).getTime()
                : 0;
            const bDate = b.latest?.scheduled_datetime
                ? new Date(b.latest.scheduled_datetime).getTime()
                : 0;
            return bDate - aDate;
        });
    };

    const groupedPreviousInterviewListings = groupPreviousInterviewsByCandidate(
        previousInterviewListings,
    );
    const togglePreviousRow = (candidateKey) => {
        setExpandedPreviousRows((prev) => ({
            ...prev,
            [candidateKey]: !prev[candidateKey],
        }));
    };

    const todayStart = (todayCurrentPage - 1) * todayItemsPerPage;
    const todayItemsToDisplay = todayInterviewListings.slice(
        todayStart,
        todayStart + todayItemsPerPage,
    );
    const totalTodayPages = Math.ceil(
        todayInterviewListings.length / todayItemsPerPage,
    );

    const allStart = (allCurrentPage - 1) * allItemsPerPage;
    const allItemsToDisplay = allInterviewListings.slice(
        allStart,
        allStart + allItemsPerPage,
    );
    const totalAllPages = Math.ceil(
        allInterviewListings.length / allItemsPerPage,
    );

    // const totalPreviousPages = Math.ceil(
    //     previousInterviewListings.length / previousItemsPerPage
    // );

    // const previousItemsToDisplay = previousInterviewListings.slice(
    //     (previousCurrentPage - 1) * previousItemsPerPage,
    //     previousCurrentPage * previousItemsPerPage
    // );

    // 4) Replace previousItemsToDisplay calculation (paginate by candidate groups, not raw rows)
    const previousItemsToDisplay = groupedPreviousInterviewListings.slice(
        (previousCurrentPage - 1) * previousItemsPerPage,
        previousCurrentPage * previousItemsPerPage,
    );

    const totalPreviousPages = Math.ceil(
        groupedPreviousInterviewListings.length / previousItemsPerPage,
    );

    const buildPageNumbers = (currentPage, totalPages) => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
            return pages;
        }
        pages.push(1);
        if (currentPage > 3) pages.push("...");
        for (
            let i = Math.max(2, currentPage - 1);
            i <= Math.min(totalPages - 1, currentPage + 1);
            i++
        ) {
            pages.push(i);
        }
        if (currentPage < totalPages - 2) pages.push("...");
        pages.push(totalPages);
        return pages;
    };

    const handleRowSelect = (row) => {
        setSelectedRows((prev) =>
            prev.includes(row) ? prev.filter((r) => r !== row) : [...prev, row],
        );
    };

    const handleSelectAll = (isChecked, itemsToDisplay) => {
        setSelectedRows(isChecked ? itemsToDisplay : []);
    };

    const handleRescheduleInterview = async () => {

        if (!newInterviewDate) {
            setNewInterviewDateError("Please select New Interview Time");
            return;
        }

        setNewInterviewDateError("");
        try {
            const response = await functionService.rescheduleInterview({
                interview_id: selectedInterview.id,
                new_datetime: newInterviewDate.toISOString(),
                reschedule_type: rescheduleType,
                reason,
            });

            console.log(response);

            message.success("Interview rescheduled successfully");

            closeInterviewDrawer();

            fetchInterviewListings();
        }
        catch (err) {
            console.log(err);
            console.log(err.response);
            console.log(err.response?.data);

            message.error(
                err.response?.data?.message ||
                "Unable to reschedule interview"
            );
        }
    };

    const handleEditInterviewDateTime = async () => {
        if (!editInterviewDateTime) {
            setEditInterviewDateTimeError(
                "Please select Interview Date & Time"
            );
            return;
        }

        setEditInterviewDateTimeError("");

        try {
            const response =
                await functionService.editInterviewDateTime({
                    interview_id: selectedEditInterview.id,
                    new_datetime:
                        editInterviewDateTime.toISOString(),
                });

            if (!response.status) {
                throw response.error;
            }

            message.success(
                "Interview date & time updated successfully"
            );

            closeEditInterviewDateTime();

            fetchInterviewListings();

        } catch (err) {
            console.error(err);

            message.error(
                err?.response?.data?.message ||
                "Unable to update interview date & time"
            );
        }
    };


    const handleInterviewConductedDirect = async (
        item,
        conducted
    ) => {
        try {
            await functionService.post(
                `demand/update-interview-conducted/${item.id}`,
                {
                    interview_conducted: conducted,
                    not_conducted_type: null
                }
            );

            message.success(
                "Interview updated successfully"
            );

            fetchInterviewListings();

        } catch (err) {
            message.error(
                err?.response?.data?.message ||
                "Unable to update interview status"
            );
        }
    };


    const submitInterviewNotConducted = async () => {

        if (!notConductedType) {
            message.error(
                "Please select reason type"
            );
            return;
        }

        try {

            await functionService.post(
                `demand/update-interview-conducted/${selectedInterviewConducted.id}`,
                {
                    interview_conducted: false,
                    not_conducted_type: notConductedType
                }
            );

            message.success(
                "Interview updated successfully"
            );

            setIsInterviewNotConductedModalVisible(false);

            setNotConductedType(null);

            fetchInterviewListings();

        } catch (err) {

            message.error(
                err?.response?.data?.message ||
                "Unable to update interview status"
            );
        }
    };


    const openInterviewDrawer = (item) => {
        setSelectedInterview(item);

        // default selection
        setRescheduleType("Candidate Led");

        setReason("");
        setNewInterviewDate(null);

        setIsInterviewDrawerVisible(true);
    };

    const closeInterviewDrawer = () => {

        setSelectedInterview(null);

        setRescheduleType(null);

        setNewInterviewDate(null);

        setReason("");

        setIsInterviewDrawerVisible(false);
    };

    const openEditInterviewDateTime = (item) => {
        setSelectedEditInterview(item);

        setEditInterviewDateTime(
            item.scheduled_datetime
                ? dayjs(item.scheduled_datetime)
                : null
        );

        setEditInterviewDateTimeError("");

        setIsEditInterviewDateTimeVisible(true);
    };

    const closeEditInterviewDateTime = () => {
        setSelectedEditInterview(null);
        setEditInterviewDateTime(null);
        setEditInterviewDateTimeError("");
        setIsEditInterviewDateTimeVisible(false);
    };

    const openInterviewNotConductedModal = (item) => {
        setSelectedInterviewConducted(item);
        setNotConductedType(null);
        setIsInterviewNotConductedModalVisible(true);
    };

    const openInterviewConductedDrawer = (item) => {
        setSelectedInterviewConducted(item);

        setInterviewConducted(null);

        setNotConductedType(null);

        setIsInterviewConductedDrawerVisible(true);
    };

    const closeInterviewConductedDrawer = () => {
        setSelectedInterviewConducted(null);

        setInterviewConducted(null);

        setNotConductedType(null);

        setIsInterviewConductedDrawerVisible(false);
    };

    const openProfileStatusDrawer = (item) => {
        setSelectedProfileCandidate(item);

        setUpdateCandidateStatus(item.candidate_current_status || null);

        setUpdateClientRemark(item.remarks || "");

        setClientFeedback(item.client_feedback || "");

        setInterviewDateTime(
            item.scheduled_datetime
                ? dayjs(item.scheduled_datetime)
                : null
        );

        setIsProfileDrawerVisible(true);
    };

    const closeProfileStatusDrawer = () => {
        setSelectedProfileCandidate(null);
        setIsProfileDrawerVisible(false);
    };

    const getStatusChangedDateFromHistory = (status, statusHistory) => {
        if (!status || !Array.isArray(statusHistory)) return null;

        const matchingEntries = statusHistory.filter(
            (entry) =>
                entry.status === status &&
                entry.changed_at
        );

        if (matchingEntries.length === 0) return null;

        const latestEntry =
            matchingEntries.reduce(
                (latest, entry) => {
                    if (!latest) return entry;

                    return dayjs(
                        entry.changed_at
                    ).isAfter(
                        dayjs(
                            latest.changed_at
                        )
                    )
                        ? entry
                        : latest;
                },
                null
            );

        return latestEntry?.changed_at
            ? dayjs(
                latestEntry.changed_at
            )
            : null;
    };
    const menu = (item) => {

        const normalizedStatus =
            (item.candidate_status || "")
                .toLowerCase()
                .trim();

        const latestInterviewConducted =
            item.interview_conducted?.[
            item.interview_conducted.length - 1
            ];

        const isInterviewConducted =
            latestInterviewConducted?.interview_conducted === true;

        const isInterviewConductedEmpty =
            !latestInterviewConducted ||
            latestInterviewConducted.interview_conducted === null ||
            latestInterviewConducted.interview_conducted === undefined;

        const canReschedule =
            !normalizedStatus.includes("back out") &&
            !normalizedStatus.includes("backout") &&
            !normalizedStatus.includes("rejected") &&
            !isInterviewConducted;

        const isTodayOrPastInterview =
            item.scheduled_datetime &&
            dayjs(item.scheduled_datetime).startOf("day") <= dayjs().startOf("day");

        return (
            <Menu
                style={{
                    minWidth: "32px",
                    borderRadius: "10px",
                    padding: "6px",
                    boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
                }}
            >
                {canReschedule && (
                    <Menu.Item
                        key="updateInterview"
                        onClick={() => openInterviewDrawer(item)}
                    >
                        Reschedule Interview
                    </Menu.Item>
                )}

                <Menu.Item
                    key="editInterviewDateTime"
                    onClick={() => openEditInterviewDateTime(item)}
                >
                    Edit Date & Time
                </Menu.Item>

                <Menu.Item
                    key="updateProfileStatus"
                    onClick={() => openProfileStatusDrawer(item)}
                >
                    Update Profile Status
                </Menu.Item>

                {isTodayOrPastInterview && isInterviewConductedEmpty && (
                    <Menu.SubMenu
                        key="interviewConducted"
                        title="Interview Conducted"
                    >
                        <Menu.Item
                            key="interviewYes"
                            onClick={() =>
                                handleInterviewConductedDirect(
                                    item,
                                    true
                                )
                            }
                        >
                            Yes
                        </Menu.Item>

                        <Menu.Item
                            key="interviewNo"
                            onClick={() =>
                                openInterviewNotConductedModal(item)
                            }
                        >
                            No
                        </Menu.Item>
                    </Menu.SubMenu>
                )}
            </Menu>
        );
    };
    const exportToCSV = () => {
        const baseData = showInterviewHistory
            ? previousInterviewListings
            : selectedRows.length > 0
                ? selectedRows
                : filteredJobListings;

        const headers = [
            "Candidate Details",
            "Candidate Email",
            "Candidate Contact",
            "Round",
            "Interview Date & Time",
            "Account Name",
            "Role",
            "Recruiter",
            "Profile Sent Date",
            "Candidate Status",
            "Interview Conducted",
            "Reschedule Type",
            "Reschedule History",
            "Debrief",
            "Resume",
            "Miscellaneous Files",
        ];

        const escape = (val) => {
            const str =
                val === null || val === undefined
                    ? "-"
                    : String(val);

            return str.includes(",") ||
                str.includes('"') ||
                str.includes("\n")
                ? `"${str.replace(/"/g, '""')}"`
                : str;
        };

        /*
         * =====================================================
         * STEP 1:
         * Create one common array containing:
         *
         * - Current Interview
         * - Every Reschedule History
         *
         * We will sort this array later based on
         * Interview Date & Time.
         * =====================================================
         */

        const exportRows = [];

        baseData.forEach((item) => {

            /*
             * Candidate Name
             */
            const candidateName =
                `${item.first_name || ""} ${item.last_name || ""}`
                    .trim() || "-";


            /*
             * Candidate Email
             */
            const candidateEmail =
                item.email || "-";


            /*
             * Candidate Contact
             */
            const candidateContact =
                item.primary_no || "-";


            /*
             * Resume
             */
            const resume =
                item.cv_url || "-";


            /*
             * Miscellaneous File
             */
            const miscellaneousFile =
                item.misc_file_url || "-";


            /*
             * Reschedule History
             *
             * Keep every reschedule.
             */
            const reschedules =
                [...(item.interview_schedule_history || [])]
                    .filter(
                        (history) =>
                            history.action === "Rescheduled"
                    );


            /*
             * Latest Interview Conducted Status
             */
            const latestConducted =
                [...(item.interview_conducted || [])]
                    .sort(
                        (a, b) =>
                            new Date(b.recorded_at) -
                            new Date(a.recorded_at)
                    )[0];


            let interviewConducted = "-";

            if (
                latestConducted &&
                latestConducted.interview_conducted === true
            ) {
                interviewConducted = "Yes";
            }

            else if (
                latestConducted &&
                latestConducted.interview_conducted === false
            ) {
                interviewConducted =
                    `No${latestConducted.not_conducted_type
                        ? ` (${latestConducted.not_conducted_type})`
                        : ""
                    }`;
            }


            /*
             * Debrief Status
             */
            const debriefStatus =
                item.debrief_form &&
                    item.debrief_form.length > 0
                    ? "Completed"
                    : "-";


            /*
             * =====================================================
             * CURRENT INTERVIEW
             * =====================================================
             */

            exportRows.push({
                sortDate: item.scheduled_datetime
                    ? new Date(item.scheduled_datetime)
                    : new Date(0),

                row: [

                    // Candidate Details
                    escape(candidateName),

                    // Candidate Email
                    escape(candidateEmail),

                    // Candidate Contact
                    escape(candidateContact),

                    // Round
                    escape(
                        item.round_name || "-"
                    ),

                    // Interview Date & Time
                    escape(
                        item.scheduled_datetime
                            ? formatDateTime(
                                item.scheduled_datetime
                            )
                            : "-"
                    ),

                    // Account Name
                    escape(
                        item.company_name || "-"
                    ),


                    // Role
                    escape(
                        item.role_title || "-"
                    ),

                    // Recruiter
                    escape(
                        item.recruiter_name || "-"
                    ),

                    // Profile Sent Date
                    escape(
                        item.profile_sent_date
                            ? formatDateTime(
                                item.profile_sent_date
                            )
                            : "-"
                    ),

                    // Candidate Status
                    escape(
                        item.candidate_status || "-"
                    ),

                    // Interview Conducted
                    escape(
                        interviewConducted
                    ),

                    // Reschedule Type
                    "-",

                    // Reschedule History
                    reschedules.length > 0
                        ? `${reschedules.length} Reschedule${reschedules.length > 1
                            ? "s"
                            : ""
                        }`
                        : "-",

                    // Debrief
                    escape(
                        debriefStatus
                    ),

                    // Resume
                    escape(resume),

                    // Miscellaneous Files
                    escape(
                        miscellaneousFile
                    ),
                ],
            });


            /*
             * =====================================================
             * RESCHEDULE HISTORY
             * =====================================================
             *
             * One row for every reschedule.
             *
             * We use old_scheduled_datetime
             * as the interview date represented by
             * this reschedule row.
             * =====================================================
             */

            reschedules.forEach((history) => {

                exportRows.push({

                    sortDate:
                        history.old_scheduled_datetime
                            ? new Date(
                                history.old_scheduled_datetime
                            )
                            : new Date(0),

                    row: [

                        // Candidate Details
                        escape(candidateName),

                        // Candidate Email
                        escape(candidateEmail),

                        // Candidate Contact
                        escape(candidateContact),

                        // Round
                        escape(
                            item.round_name || "-"
                        ),

                        // Old Interview Date & Time
                        escape(
                            history.old_scheduled_datetime
                                ? formatDateTime(
                                    history.old_scheduled_datetime
                                )
                                : "-"
                        ),

                        // Account Name
                        escape(
                            item.company_name || "-"
                        ),


                        // Role
                        escape(
                            item.role_title || "-"
                        ),

                        // Recruiter
                        escape(
                            item.recruiter_name || "-"
                        ),

                        // Profile Sent Date
                        escape(
                            item.profile_sent_date
                                ? formatDateTime(
                                    item.profile_sent_date
                                )
                                : "-"
                        ),

                        // Candidate Status
                        escape(
                            item.candidate_status || "-"
                        ),

                        // Interview Conducted
                        "-",

                        // Reschedule Type
                        escape(
                            history.reschedule_type || "-"
                        ),

                        // Reschedule History
                        escape(
                            history.recorded_at
                                ? `Rescheduled on ${formatDateTime(
                                    history.recorded_at
                                )}`
                                : "Rescheduled"
                        ),

                        // Debrief
                        "-",

                        // Resume
                        escape(resume),

                        // Miscellaneous Files
                        escape(
                            miscellaneousFile
                        ),
                    ],
                });
            });
        });


        /*
         * =====================================================
         * STEP 2:
         * SORT ALL ROWS BY INTERVIEW DATE & TIME
         *
         * Oldest interview first.
         * =====================================================
         */

        exportRows.sort(
            (a, b) =>
                a.sortDate.getTime() -
                b.sortDate.getTime()
        );


        /*
         * =====================================================
         * STEP 3:
         * Convert sorted rows into CSV
         * =====================================================
         */

        const rows = [
            headers.join(","),
            ...exportRows.map(
                (item) =>
                    item.row.join(",")
            ),
        ];


        /*
         * =====================================================
         * STEP 4:
         * Download CSV
         * =====================================================
         */

        const blob = new Blob(
            [rows.join("\n")],
            {
                type: "text/csv;charset=utf-8;",
            }
        );

        const url =
            URL.createObjectURL(blob);

        const a =
            document.createElement("a");

        a.href = url;

        a.download =
            `interview_table_${new Date()
                .toISOString()
                .split("T")[0]
            }.csv`;

        document.body.appendChild(a);

        a.click();

        document.body.removeChild(a);

        URL.revokeObjectURL(url);

        setSelectedRows([]);
    };

    const getStatusStyle = (status) => {
        const baseStyle = {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "6px 12px",
            borderRadius: "9999px",
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: 1,
            whiteSpace: "nowrap",
            letterSpacing: "0.2px",
            border: "1px solid transparent",
        };

        const normalized = (status || "").toLowerCase().trim();

        if (!normalized) {
            return {
                label: "Pending",
                style: {
                    ...baseStyle,
                    background: "#F3F4F6",
                    color: "#4B5563",
                    borderColor: "#E5E7EB",
                },
            };
        }

        if (normalized.includes("yet to be interviewed")) {
            return {
                label: status,
                style: {
                    ...baseStyle,
                    background: "#ECFDF3",
                    color: "#166534",
                    borderColor: "#BBF7D0",
                },
            };
        }

        if (normalized.includes("interview scheduled")) {
            return {
                label: status,
                style: {
                    ...baseStyle,
                    background: "#DCFCE7",
                    color: "#15803D",
                    borderColor: "#86EFAC",
                },
            };
        }

        if (normalized.includes("interviewed - feedback pending")) {
            return {
                label: status,
                style: {
                    ...baseStyle,
                    background: "#D1FAE5",
                    color: "#065F46",
                    borderColor: "#6EE7B7",
                },
            };
        }

        if (normalized.includes("during interview back out")) {
            return {
                label: status,
                style: {
                    ...baseStyle,
                    background: "#FEE2E2",
                    color: "#B91C1C",
                    borderColor: "#FECACA",
                },
            };
        }

        if (normalized.includes("interviewed - rejected")) {
            return {
                label: status,
                style: {
                    ...baseStyle,
                    background: "#FEE2E2",
                    color: "#7F1D1D",
                    borderColor: "#FCA5A5",
                },
            };
        }

        return {
            label: status,
            style: {
                ...baseStyle,
                background: "#F3F4F6",
                color: "#4B5563",
                borderColor: "#E5E7EB",
            },
        };
    };

    // Add these style helpers inside InterviewTable (near other style constants/helpers)
    const previousRowUXStyles = {
        expandableDefault: "#FECACA",
        expandableHover: "#dcfce7",
        expandableExpanded: "#dcfce7",
        nonExpandable: "#FFFFFF",
        childRow: "#FAFCFF",
    };

    const getPreviousParentRowBg = ({
        isExpandable,
        isExpanded,
        isSelected,
        isHovered,
    }) => {
        if (isSelected) return "#d0e7ff";
        if (!isExpandable) return previousRowUXStyles.nonExpandable;
        if (isExpanded) return previousRowUXStyles.expandableExpanded;
        if (isHovered) return previousRowUXStyles.expandableHover;
        return previousRowUXStyles.expandableDefault;
    };
    // const getRowBackground = (status = "") => {
    //     const normalized = status.toLowerCase().trim();

    //     // if (normalized.includes("yet to be interviewed")) {
    //     //   return "#DCFCE7"; // light green
    //     // }

    //     // if (normalized.includes("interview scheduled")) {
    //     //   return "#F0FDF4"; // very light green
    //     // }

    //     // if (normalized.includes("interviewed - feedback pending")) {
    //     //   return "#D1FAE5"; // darker green
    //     // }

    //     // if (normalized.includes("during interview back out")) {
    //     //   return "#FEF2F2"; // light red
    //     // }

    //     // if (normalized.includes("interviewed - rejected")) {
    //     //   return "#FEE2E2"; // red
    //     // }

    //     // return "#FFFFFF";

    //     if (normalized.includes("yet to be interviewed")) {
    //         return "#FEF8E7"; // pale yellow -> waiting for next round
    //     }

    //     if (normalized.includes("interview scheduled")) {
    //         return "#DCFCE7"; // light green -> scheduled
    //     }

    //     if (normalized.includes("interviewed - feedback pending")) {
    //         return "#BBF7D0"; // medium green -> interview completed
    //     }

    //     if (normalized.includes("during interview back out")) {
    //         return "#FCA5A5"; // soft red -> candidate dropped during process
    //     }

    //     if (normalized.includes("interviewed - rejected")) {
    //         return "#FCA5A5"; // soft red -> rejected in round
    //     }

    //     if (normalized.includes("post interview back out")) {
    //         return "#FECACA"; // slightly darker red -> dropped after interviews
    //     }

    //     if (normalized.includes("final reject")) {
    //         return "#f08484"; // strong red -> final rejection
    //     }

    //     if (normalized.includes("final select")) {
    //         return "#86EFAC"; // strong green -> selected
    //     }

    //     if (normalized.includes("offer released")) {
    //         return "#BFDBFE"; // light blue -> offer stage started
    //     }

    //     if (normalized.includes("offer yet to be released")) {
    //         return "#DBEAFE"; // pale blue -> waiting for offer
    //     }

    //     if (normalized.includes("offer accepted")) {
    //         return "#6EE7B7"; // green-teal -> accepted offer
    //     }

    //     if (normalized.includes("offer rejected")) {
    //         return "#FCA5A5"; // red -> rejected offer
    //     }

    //     if (normalized.includes("post offer back out")) {
    //         return "#FECACA"; // red -> backed out after offer
    //     }

    //     if (normalized.includes("joined")) {
    //         return "#22C55E"; // strong green -> successful closure
    //     }

    //     if (normalized.includes("not joined")) {
    //         return "#FB7185"; // pink-red -> accepted but did not join
    //     }

    //     return "#FFFFFF";
    // };

    const getRowBackground = (status = "") => {
        const normalized = status.toLowerCase().trim();

        // Pending / In Progress
        if (
            normalized.includes("yet to be interviewed") ||
            normalized.includes("interview scheduled") ||
            normalized.includes("feedback pending") ||
            normalized.includes("offer yet to be released") ||
            normalized.includes("final select") ||
            normalized.includes("offer released") ||
            normalized.includes("offer accepted") ||
            normalized.includes("joined")
        ) {
            return "#DCFCE7";
        }

        // Negative / Closed
        if (
            normalized.includes("back out") ||
            normalized.includes("backout") ||
            normalized.includes("rejected") ||
            normalized.includes("final reject") ||
            normalized.includes("not joined")
        ) {
            return "#FECACA";
        }

        // Neutral
        return "#FFFFFF";
    };

    const StatusBadge = ({ status }) => {
        const { label, style } = getStatusStyle(status);

        return <span style={style}>{label}</span>;
    };
    const adminAccess = Number(localStorage.getItem("adminAccess"));

    const renderTableSection = (
        title,
        listings,
        itemsToDisplay,
        currentPage,
        setCurrentPage,
        totalPages,
        itemsPerPage,
        setItemsPerPage,
        disablePagination = false,
        isUpcomingTable = false
    ) => {
        const showEmptyState = listings.length === 0;
        const showCheckbox = adminAccess !== 3;

        const candidateLeft = showCheckbox ? "40px" : "0px";
        const roundLeft = showCheckbox ? "240px" : "200px";
        const interviewLeft = showCheckbox ? "330px" : "290px";
        return (
            <>
                <h2
                    style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "#1F2937",
                        margin: "28px 0 12px",
                        paddingBottom: "8px",
                        borderBottom: "2px solid #4F46E5",
                        display: "inline-block",
                    }}
                >
                    {title}
                </h2>

                <Styled.TableWrap
                    style={{
                        overflowX: "auto",
                        overflowY: "auto",
                        maxHeight: "calc(100vh - 315px)",
                    }}
                >
                    <Styled.MainTable>
                        <thead
                            style={{
                                position: "sticky",
                                top: 0,
                                zIndex: 20,
                                background: "#F2F6F8",
                            }}
                        >
                            <tr>
                                {showCheckbox && (
                                    <th
                                        style={{
                                            position: "sticky",
                                            left: 0,
                                            top: 0,
                                            zIndex: 11,
                                            background: "#F2F6F8",
                                            minWidth: "40px",
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            onChange={(e) =>
                                                handleSelectAll(e.target.checked, itemsToDisplay)
                                            }
                                            checked={
                                                itemsToDisplay.length > 0 &&
                                                itemsToDisplay.every((row) =>
                                                    selectedRows.includes(row),
                                                )
                                            }
                                        />
                                    </th>
                                )}

                                <th
                                    style={{
                                        position: "sticky",
                                        left: candidateLeft,
                                        top: 0,
                                        zIndex: 11,
                                        background: "#F2F6F8",
                                        minWidth: "200px",
                                    }}
                                >
                                    Candidate details
                                </th>
                                <th
                                    style={{
                                        position: "sticky",
                                        left: roundLeft,
                                        top: 0,
                                        zIndex: 11,
                                        background: "#F2F6F8",
                                        minWidth: "90px",
                                    }}
                                >
                                    Round
                                </th>
                                <th
                                    style={{
                                        position: "sticky",
                                        left: interviewLeft,
                                        top: 0,
                                        zIndex: 11,
                                        background: "#F2F6F8",
                                        minWidth: "200px",
                                    }}
                                >
                                    Interview Date & Time
                                </th>
                                <th>Account Name</th>
                                {/* <th>Sub Account</th>
                                    <th>End Client</th> */}
                                <th>Role</th>
                                <th>Recruiter</th>
                                <th>Profile Sent Date</th>
                                <th>Candidate Status</th>
                                <th>Interview Conducted</th>
                                <th>Reschedule Type</th>
                                <th>Reschedule History</th>
                                <th>Debrief History</th>
                                <th>Debrief</th>
                                <th style={{ background: "#F2F6F8" }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {itemsToDisplay.map((item, index) => {
                                // const rowBg = selectedRows.includes(item) ? "#d0e7ff" : "#ffffff";
                                const rowBg = selectedRows.includes(item)
                                    ? "#d0e7ff"
                                    : getRowBackground(item.candidate_status);
                                // const entry = getLatestInterviewEntry(item.scheduled_datetime);
                                const latestReschedule =
                                    [...(item.interview_schedule_history || [])]
                                        .reverse()
                                        .find(history => history.action === "Rescheduled");
                                return (
                                    <tr key={index} style={{ background: rowBg }}>
                                        {showCheckbox && (
                                            <td
                                                style={{
                                                    position: "sticky",
                                                    left: 0,
                                                    zIndex: 1,
                                                    background: rowBg,
                                                    minWidth: "40px",
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    onChange={() => handleRowSelect(item)}
                                                    checked={selectedRows.includes(item)}
                                                />
                                            </td>
                                        )}

                                        <td
                                            style={{
                                                position: "sticky",
                                                left: candidateLeft,
                                                zIndex: 1,
                                                background: rowBg,
                                                minWidth: "200px",
                                            }}
                                        >
                                            {item.first_name} {item.last_name} <br />
                                            {item.email} <br />
                                            {item.primary_no}
                                        </td>
                                        <Tooltip
                                            title={item.round_name === "DR" ? "Discussion Round" : ""}
                                        >
                                            <td
                                                style={{
                                                    position: "sticky",
                                                    left: roundLeft,
                                                    zIndex: 1,
                                                    background: rowBg,
                                                    minWidth: "90px",
                                                    fontWeight: 600,
                                                    color: "#1B49EA",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                {item.round_name || "-"}
                                            </td>
                                        </Tooltip>

                                        <td
                                            style={{
                                                position: "sticky",
                                                left: interviewLeft,
                                                zIndex: 1,
                                                background: rowBg,
                                                minWidth: "200px",
                                            }}
                                        >
                                            {item.scheduled_datetime
                                                ? new Date(item.scheduled_datetime).toDateString() ===
                                                    todayDateStr
                                                    ? formatTime(item.scheduled_datetime)
                                                    : formatDateTime(item.scheduled_datetime)
                                                : "-"}
                                        </td>
                                        <td>{item.company_name || "-"}</td>
                                        {/* <td>{item.sub_account || "-"}</td>
                                            <td>{item.end_client || "-"}</td> */}

                                        <td>{item.role_title || "-"}</td>
                                        <td>{item.recruiter_name || "-"}</td>
                                        <td>
                                            {item.profile_sent_date
                                                ? formatDate(item.profile_sent_date)
                                                : "-"}
                                        </td>
                                        <td>
                                            {item.candidate_status ? (
                                                item.candidate_status
                                            ) : (
                                                <span style={{ color: "#b45309", fontWeight: 600 }}>
                                                    Pending
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            {(() => {

                                                const latest =
                                                    item.interview_conducted?.[
                                                    item.interview_conducted.length - 1
                                                    ];

                                                if (
                                                    !latest ||
                                                    latest.interview_conducted === null ||
                                                    latest.interview_conducted === undefined
                                                ) {
                                                    return "-";
                                                }

                                                if (latest.interview_conducted === true) {
                                                    return (
                                                        <span
                                                            style={{
                                                                color: "#16A34A",
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            Yes
                                                        </span>
                                                    );
                                                }

                                                return (
                                                    <span
                                                        style={{
                                                            color: "#DC2626",
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        No
                                                        {latest.not_conducted_type
                                                            ? ` (${latest.not_conducted_type})`
                                                            : ""}
                                                    </span>
                                                );

                                            })()}
                                        </td>
                                        <td>
                                            {latestReschedule ? (
                                                <Tooltip title={latestReschedule.reason ? `Reason: ${latestReschedule.reason}` : "Reason: -"}>
                                                    <span
                                                        style={{
                                                            cursor: "pointer",
                                                            color: "#1677ff",
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        {latestReschedule.reschedule_type || "-"}
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                "-"
                                            )}
                                        </td>

                                        <td>
                                            {(() => {
                                                const sortedReschedules =
                                                    [...(item.interview_schedule_history || [])]
                                                        .filter(
                                                            (history) =>
                                                                history.action === "Rescheduled"
                                                        )
                                                        .sort(
                                                            (a, b) =>
                                                                new Date(b.recorded_at) -
                                                                new Date(a.recorded_at)
                                                        );

                                                return sortedReschedules.length > 0 ? (
                                                    <Dropdown
                                                        menu={{
                                                            items: [
                                                                {
                                                                    key: "reschedule-history",
                                                                    label: (
                                                                        <div
                                                                            style={{
                                                                                width: "400px",
                                                                                padding: "4px",
                                                                            }}
                                                                        >
                                                                            {/* Title */}
                                                                            <div
                                                                                style={{
                                                                                    fontSize: "14px",
                                                                                    fontWeight: 700,
                                                                                    color: "#111827",
                                                                                    marginBottom: "16px",
                                                                                    paddingBottom: "10px",
                                                                                    borderBottom:
                                                                                        "1px solid #E5E7EB",
                                                                                }}
                                                                            >
                                                                                Reschedule History
                                                                            </div>

                                                                            {/* History List */}
                                                                            <div
                                                                                style={{
                                                                                    maxHeight: "350px",
                                                                                    overflowY: "auto",
                                                                                    paddingRight: "4px",
                                                                                }}
                                                                            >
                                                                                {sortedReschedules.map(
                                                                                    (history, index) => (
                                                                                        <div
                                                                                            key={index}
                                                                                            style={{
                                                                                                position: "relative",
                                                                                                padding: "14px",
                                                                                                marginBottom:
                                                                                                    index !==
                                                                                                        sortedReschedules.length - 1
                                                                                                        ? "10px"
                                                                                                        : "0",
                                                                                                background:
                                                                                                    "#FFFFFF",
                                                                                                border:
                                                                                                    "1px solid #E5E7EB",
                                                                                                borderRadius:
                                                                                                    "8px",
                                                                                                boxShadow:
                                                                                                    "0 1px 2px rgba(0, 0, 0, 0.04)",
                                                                                            }}
                                                                                        >
                                                                                            {/* Old Interview Date */}
                                                                                            <div
                                                                                                style={{
                                                                                                    marginBottom:
                                                                                                        "12px",
                                                                                                }}
                                                                                            >
                                                                                                <div
                                                                                                    style={{
                                                                                                        fontSize:
                                                                                                            "10px",
                                                                                                        fontWeight: 700,
                                                                                                        color:
                                                                                                            "#6B7280",
                                                                                                        textTransform:
                                                                                                            "uppercase",
                                                                                                        marginBottom:
                                                                                                            "4px",
                                                                                                        letterSpacing:
                                                                                                            "0.3px",
                                                                                                    }}
                                                                                                >
                                                                                                    Old Interview Date
                                                                                                </div>

                                                                                                <div
                                                                                                    style={{
                                                                                                        fontSize:
                                                                                                            "14px",
                                                                                                        fontWeight: 700,
                                                                                                        color:
                                                                                                            "#111827",
                                                                                                    }}
                                                                                                >
                                                                                                    {history.old_scheduled_datetime
                                                                                                        ? new Date(
                                                                                                            history.old_scheduled_datetime
                                                                                                        ).toLocaleString(
                                                                                                            "en-GB",
                                                                                                            {
                                                                                                                day: "2-digit",
                                                                                                                month: "long",
                                                                                                                year: "numeric",
                                                                                                                hour: "2-digit",
                                                                                                                minute: "2-digit",
                                                                                                                hour12: true,
                                                                                                            }
                                                                                                        )
                                                                                                        : "-"}
                                                                                                </div>
                                                                                            </div>

                                                                                            {/* Reschedule Type */}
                                                                                            <div
                                                                                                style={{
                                                                                                    marginBottom:
                                                                                                        "12px",
                                                                                                }}
                                                                                            >
                                                                                                <span
                                                                                                    style={{
                                                                                                        display:
                                                                                                            "inline-block",
                                                                                                        padding:
                                                                                                            "4px 9px",
                                                                                                        borderRadius:
                                                                                                            "5px",
                                                                                                        fontSize:
                                                                                                            "11px",
                                                                                                        fontWeight: 600,
                                                                                                        color:
                                                                                                            history.reschedule_type ===
                                                                                                                "Candidate Led"
                                                                                                                ? "#2563EB"
                                                                                                                : "#7C3AED",
                                                                                                        background:
                                                                                                            history.reschedule_type ===
                                                                                                                "Candidate Led"
                                                                                                                ? "#EFF6FF"
                                                                                                                : "#F5F3FF",
                                                                                                    }}
                                                                                                >
                                                                                                    {history.reschedule_type ||
                                                                                                        "-"}
                                                                                                </span>
                                                                                            </div>

                                                                                            {/* Reason */}
                                                                                            {history.reason && (
                                                                                                <div>
                                                                                                    <div
                                                                                                        style={{
                                                                                                            fontSize: "10px",
                                                                                                            fontWeight: 700,
                                                                                                            color: "#6B7280",
                                                                                                            textTransform: "uppercase",
                                                                                                            marginBottom: "4px",
                                                                                                            letterSpacing: "0.3px",
                                                                                                        }}
                                                                                                    >
                                                                                                        Reason
                                                                                                    </div>

                                                                                                    <div
                                                                                                        style={{
                                                                                                            fontSize: "12px",
                                                                                                            color: "#374151",
                                                                                                            lineHeight: "1.5",
                                                                                                            wordBreak: "break-word",
                                                                                                        }}
                                                                                                    >
                                                                                                        {history.reason}
                                                                                                    </div>
                                                                                                </div>
                                                                                            )}
                                                                                        </div>
                                                                                    )
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ),
                                                                    disabled: true,
                                                                },
                                                            ],
                                                        }}
                                                        trigger={["click"]}
                                                        placement="bottomRight"
                                                    >
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            onClick={(e) =>
                                                                e.preventDefault()
                                                            }
                                                            style={{
                                                                fontSize: "11px",
                                                                color: "#1B49EA",
                                                                padding: "3px 8px",
                                                                height: "26px",
                                                                fontWeight: 600,
                                                                border:
                                                                    "1px solid #BFDBFE",
                                                                borderRadius: "6px",
                                                                background: "#EFF6FF",
                                                            }}
                                                        >
                                                            View History

                                                            <DownOutlined
                                                                style={{
                                                                    fontSize: "9px",
                                                                    marginLeft: "5px",
                                                                }}
                                                            />
                                                        </Button>
                                                    </Dropdown>
                                                ) : (
                                                    "-"
                                                );
                                            })()}
                                        </td>

                                        <td>
                        {(() => {
                            const candidateId = item.candidate_id;
                            const cached = debriefHistoryByCandidate[candidateId];

                            // Shared outer "card/popover" wrapper used by every content state.
                            const CONTAINER_STYLE = {
                                width: "400px",
                                boxSizing: "border-box",
                                padding: "16px",
                            };

                            const handleOpenChange = (open) => {
                                if (!open) return;
                                const existing = debriefHistoryByCandidate[candidateId];
                                if (existing && (existing.status === "loading" || existing.status === "success")) {
                                    return; // already fetched/in-flight — avoid duplicate calls
                                }

                                setDebriefHistoryByCandidate((prev) => ({
                                    ...prev,
                                    [candidateId]: { status: "loading", data: [] },
                                }));

                                functionService
                                    .getDebriefHistory({ candidate_id: candidateId })
                                    .then((result) => {
                                        if (!result.status) {
                                            setDebriefHistoryByCandidate((prev) => ({
                                                ...prev,
                                                [candidateId]: { status: "error", data: [] },
                                            }));
                                            return;
                                        }

                                        const payload = result.response?.data;
                                        const rawList = Array.isArray(payload?.data)
                                            ? payload.data
                                            : Array.isArray(payload)
                                                ? payload
                                                : [];

                                        const historyList = rawList.map((h) => ({
                                            ...h,
                                            candidate_name:
                                                h.candidate_name ||
                                                `${h.first_name || ""} ${h.last_name || ""}`.trim() ||
                                                "-",
                                        }));

                                        setDebriefHistoryByCandidate((prev) => ({
                                            ...prev,
                                            [candidateId]: { status: "success", data: historyList },
                                        }));
                                    })
                                    .catch(() => {
                                        setDebriefHistoryByCandidate((prev) => ({
                                            ...prev,
                                            [candidateId]: { status: "error", data: [] },
                                        }));
                                    });
                            };

                            const renderContent = () => {
                                // Not fetched yet — nothing to show before the first click.
                                if (!cached) {
                                    return null;
                                }

                                if (cached.status === "loading") {
                                    return (
                                        <div
                                            style={{
                                                ...CONTAINER_STYLE,
                                                padding: "28px 16px",
                                                textAlign: "center",
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                color: "#6B7280",
                                            }}
                                        >
                                            Loading debrief history...
                                        </div>
                                    );
                                }

                                if (cached.status === "error") {
                                    return (
                                        <div
                                            style={{
                                                ...CONTAINER_STYLE,
                                                padding: "28px 16px",
                                                textAlign: "center",
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                color: "#DC2626",
                                            }}
                                        >
                                            Failed to load debrief history. Please try again.
                                        </div>
                                    );
                                }

                                const historyList = (cached.data || []).filter(
                                    (h) => Array.isArray(h.debrief_form) && h.debrief_form.length > 0
                                );

                                if (historyList.length === 0) {
                                    return renderDebriefHistoryEmpty(CONTAINER_STYLE);
                                }

                                return (
                                    <div style={CONTAINER_STYLE}>
                                        <div
                                            style={{
                                                fontSize: "15px",
                                                fontWeight: 700,
                                                color: "#111827",
                                                marginBottom: "14px",
                                                paddingTop: "2px",
                                                paddingBottom: "12px",
                                                borderBottom: "1px solid #E5E7EB",
                                            }}
                                        >
                                            Debrief History
                                        </div>

                                        <div
                                            style={{
                                                maxHeight: "350px",
                                                overflowY: "auto",
                                                paddingRight: "10px",
                                            }}
                                        >
                                            {historyList.map((candidateHistory, cIndex) => (
                                                <div
                                                    key={candidateHistory.candidate_id || cIndex}
                                                    style={{
                                                        marginBottom:
                                                            cIndex !== historyList.length - 1 ? "14px" : "0",
                                                        padding: "14px",
                                                        background: "#F9FAFB",
                                                        border: "1px solid #E5E7EB",
                                                        borderRadius: "10px",
                                                    }}
                                                >
                                                    {/* Candidate header */}
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "8px",
                                                            marginBottom: "12px",
                                                            paddingBottom: "10px",
                                                            borderBottom: "1px solid #E5E7EB",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                width: "24px",
                                                                height: "24px",
                                                                borderRadius: "50%",
                                                                background: "#DBEAFE",
                                                                color: "#1B49EA",
                                                                fontSize: "11px",
                                                                fontWeight: 700,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {(candidateHistory.candidate_name || "-")
                                                                .trim()
                                                                .charAt(0)
                                                                .toUpperCase()}
                                                        </div>
                                                        <div style={{ minWidth: 0 }}>
                                                            <div
                                                                style={{
                                                                    fontSize: "14px",
                                                                    fontWeight: 700,
                                                                    color: "#111827",
                                                                    lineHeight: "1.3",
                                                                }}
                                                            >
                                                                {candidateHistory.candidate_name || "-"}
                                                            </div>
                                                            {candidateHistory.email && (
                                                                <div
                                                                    style={{
                                                                        fontSize: "11px",
                                                                        fontWeight: 400,
                                                                        color: "#9CA3AF",
                                                                        marginTop: "1px",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                >
                                                                    {candidateHistory.email}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Rounds — lighter, no competing heavy borders */}
                                                    {candidateHistory.debrief_form.map((round, rIndex) => (
                                                        <div
                                                            key={rIndex}
                                                            style={{
                                                                paddingTop: rIndex !== 0 ? "12px" : "0",
                                                                marginTop: rIndex !== 0 ? "12px" : "0",
                                                                borderTop:
                                                                    rIndex !== 0 ? "1px dashed #E5E7EB" : "none",
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    marginBottom: "8px",
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        display: "inline-block",
                                                                        padding: "3px 10px",
                                                                        borderRadius: "20px",
                                                                        fontSize: "11px",
                                                                        fontWeight: 700,
                                                                        letterSpacing: "0.2px",
                                                                        color: "#1B49EA",
                                                                        background: "#EFF6FF",
                                                                        border: "1px solid #DBEAFE",
                                                                    }}
                                                                >
                                                                    {round.round_name || "-"}
                                                                </span>

                                                                {round.submitted_at && (
                                                                    <span
                                                                        style={{
                                                                            fontSize: "10px",
                                                                            fontWeight: 500,
                                                                            color: "#9CA3AF",
                                                                            textAlign: "right",
                                                                        }}
                                                                    >
                                                                        {new Date(round.submitted_at).toLocaleString(
                                                                            "en-GB",
                                                                            {
                                                                                day: "2-digit",
                                                                                month: "long",
                                                                                year: "numeric",
                                                                                hour: "2-digit",
                                                                                minute: "2-digit",
                                                                            }
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {Array.isArray(round.questions) &&
                                                                round.questions.length > 0 && (
                                                                    <div style={{ marginBottom: "10px" }}>
                                                                        <div
                                                                            style={{
                                                                                fontSize: "10px",
                                                                                fontWeight: 700,
                                                                                color: "#2563EB",
                                                                                textTransform: "uppercase",
                                                                                marginBottom: "6px",
                                                                                letterSpacing: "0.4px",
                                                                            }}
                                                                        >
                                                                            Questions
                                                                        </div>
                                                                        <ol
                                                                            style={{
                                                                                margin: 0,
                                                                                paddingLeft: "18px",
                                                                                fontSize: "12px",
                                                                                color: "#374151",
                                                                                lineHeight: "1.7",
                                                                            }}
                                                                        >
                                                                            {round.questions.map((q, qIndex) => (
                                                                                <li
                                                                                    key={qIndex}
                                                                                    style={{ wordBreak: "break-word" }}
                                                                                >
                                                                                    {q}
                                                                                </li>
                                                                            ))}
                                                                        </ol>
                                                                    </div>
                                                                )}

                                                            {round.additional_information && (
                                                                <div>
                                                                    <div
                                                                        style={{
                                                                            fontSize: "10px",
                                                                            fontWeight: 700,
                                                                            color: "#B45309",
                                                                            textTransform: "uppercase",
                                                                            marginBottom: "6px",
                                                                            letterSpacing: "0.4px",
                                                                        }}
                                                                    >
                                                                        Additional Information
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            fontSize: "12px",
                                                                            color: "#374151",
                                                                            lineHeight: "1.6",
                                                                            wordBreak: "break-word",
                                                                        }}
                                                                    >
                                                                        {round.additional_information}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            };

                            return (
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: "debrief-history",
                                                label: renderContent(),
                                                disabled: true,
                                            },
                                        ],
                                    }}
                                    trigger={["click"]}
                                    placement="bottomRight"
                                    onOpenChange={handleOpenChange}
                                >
                                    <Button
                                        type="text"
                                        size="small"
                                        style={{
                                            fontSize: "11px",
                                            color: "#1B49EA",
                                            padding: "3px 8px",
                                            height: "26px",
                                            fontWeight: 600,
                                            border: "1px solid #BFDBFE",
                                            borderRadius: "6px",
                                            background: "#EFF6FF",
                                        }}
                                    >
                                        View History
                                        <DownOutlined style={{ fontSize: "9px", marginLeft: "5px" }} />
                                    </Button>
                                </Dropdown>
                            );
                        })()}
                    </td>

                                        <td>
                                            {(() => {
                                                const sortedDebrief = [
                                                    ...(typeof item.debrief_form === "string"
                                                        ? JSON.parse(item.debrief_form)
                                                        : item.debrief_form || []),
                                                ].sort((a, b) => {
                                                    const roundA = parseInt(
                                                        (a.round_name || "").match(/\d+/)?.[0] || "999",
                                                    );
                                                    const roundB = parseInt(
                                                        (b.round_name || "").match(/\d+/)?.[0] || "999",
                                                    );
                                                    return roundA - roundB;
                                                });

                                                // NEW: Round-specific Debrief display (YES / NO / -)
                                                // Parse debrief_no_reason the same way debrief_form is parsed above
                                                const noReasonList = [
                                                    ...(typeof item.debrief_no_reason === "string"
                                                        ? JSON.parse(item.debrief_no_reason)
                                                        : item.debrief_no_reason || []),
                                                ];

                                                // NEW: extract the numeric round number regardless of "R3" vs
                                                // "Round 3" formatting - reuses the same digit-extraction
                                                // approach the existing sort above already relies on.
                                                const getRoundNumber = (roundName) =>
                                                    parseInt((roundName || "").match(/\d+/)?.[0] || "-1", 10);

                                                const currentRoundNumber = getRoundNumber(item.round_name);

                                                // NEW: does THIS row's round have a debrief entry?
                                                const currentRoundDebrief =
                                                    currentRoundNumber !== -1 &&
                                                    sortedDebrief.find(
                                                        (round) =>
                                                            getRoundNumber(round.round_name) === currentRoundNumber,
                                                    );

                                                // NEW: does THIS row's round have a NO reason entry?
                                                const currentRoundNoReason =
                                                    currentRoundNumber !== -1 &&
                                                    noReasonList.find(
                                                        (round) =>
                                                            getRoundNumber(round.round_name) === currentRoundNumber,
                                                    );

                                                return (
                                                    <>
                                                        {currentRoundDebrief ? (
                                                            // EXISTING: View Debrief dropdown - completely unchanged
                                                            <Dropdown
                                                                menu={{
                                                                    items: [
                                                                        {
                                                                            key: "content",
                                                                            label: (
                                                                                <div
                                                                                    style={{
                                                                                        maxWidth: "320px",
                                                                                        padding: "10px",
                                                                                        position: "relative",
                                                                                    }}
                                                                                >
                                                                                    <Button
                                                                                        type="text"
                                                                                        size="small"
                                                                                        icon={<CopyOutlined />}
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            const debriefText = sortedDebrief
                                                                                                .map(
                                                                                                    (round) =>
                                                                                                        `${round.round_name}\n${round.questions?.join("\n")}\n${round.additional_information ||
                                                                                                        ""
                                                                                                        }`,
                                                                                                )
                                                                                                .join("\n\n");
                                                                                            copyDebriefToClipboard(
                                                                                                debriefText,
                                                                                            );
                                                                                        }}
                                                                                        style={{
                                                                                            position: "absolute",
                                                                                            top: "6px",
                                                                                            right: "6px",
                                                                                            padding: 0,
                                                                                            width: "24px",
                                                                                            height: "24px",
                                                                                            minWidth: "24px",
                                                                                            display: "flex",
                                                                                            alignItems: "center",
                                                                                            justifyContent: "center",
                                                                                            color: "#1b49ea",
                                                                                        }}
                                                                                    />

                                                                                    <div
                                                                                        style={{
                                                                                            fontSize: "12px",
                                                                                            fontWeight: 600,
                                                                                            color: "#111827",
                                                                                            marginBottom: "8px",
                                                                                            paddingRight: "30px",
                                                                                        }}
                                                                                    >
                                                                                        Debrief Details
                                                                                    </div>

                                                                                    <div
                                                                                        style={{
                                                                                            maxHeight: "300px",
                                                                                            overflowY: "auto",
                                                                                            paddingRight: "4px",
                                                                                        }}
                                                                                    >
                                                                                        {sortedDebrief.map(
                                                                                            (round, index) => (
                                                                                                <div
                                                                                                    key={index}
                                                                                                    style={{
                                                                                                        marginBottom: "14px",
                                                                                                        background: "#f8fafc",
                                                                                                        border: "1px solid #e5e7eb",
                                                                                                        borderRadius: "10px",
                                                                                                        padding: "12px",
                                                                                                        boxShadow:
                                                                                                            "0 1px 2px rgba(0,0,0,0.04)",
                                                                                                    }}
                                                                                                >
                                                                                                    <div
                                                                                                        style={{
                                                                                                            display: "flex",
                                                                                                            justifyContent:
                                                                                                                "space-between",
                                                                                                            alignItems: "center",
                                                                                                            marginBottom: "10px",
                                                                                                            borderBottom:
                                                                                                                "1px solid #e5e7eb",
                                                                                                            paddingBottom: "8px",
                                                                                                        }}
                                                                                                    >
                                                                                                        <div
                                                                                                            style={{
                                                                                                                fontWeight: 700,
                                                                                                                fontSize: "13px",
                                                                                                                color: "#1b49ea",
                                                                                                            }}
                                                                                                        >
                                                                                                            {round.round_name}
                                                                                                        </div>

                                                                                                        <div
                                                                                                            style={{
                                                                                                                fontSize: "11px",
                                                                                                                color: "#6b7280",
                                                                                                            }}
                                                                                                        >
                                                                                                            {round.submitted_at
                                                                                                                ? new Date(
                                                                                                                    round.submitted_at,
                                                                                                                ).toLocaleString()
                                                                                                                : "No Date"}
                                                                                                        </div>
                                                                                                    </div>

                                                                                                    <div
                                                                                                        style={{
                                                                                                            fontSize: "11px",
                                                                                                            fontWeight: 600,
                                                                                                            color: "#6b7280",
                                                                                                            marginBottom: "8px",
                                                                                                            textTransform:
                                                                                                                "uppercase",
                                                                                                            letterSpacing: "0.5px",
                                                                                                        }}
                                                                                                    >
                                                                                                        Questions Asked
                                                                                                    </div>

                                                                                                    {round.questions?.map(
                                                                                                        (question, qIndex) => (
                                                                                                            <div
                                                                                                                key={qIndex}
                                                                                                                style={{
                                                                                                                    padding: "8px 10px",
                                                                                                                    background: "#fff",
                                                                                                                    border:
                                                                                                                        "1px solid #e5e7eb",
                                                                                                                    borderRadius: "6px",
                                                                                                                    marginBottom: "6px",
                                                                                                                    fontSize: "12px",
                                                                                                                    color: "#111827",
                                                                                                                    wordBreak:
                                                                                                                        "break-word",
                                                                                                                    whiteSpace: "normal",
                                                                                                                    lineHeight: "1.5",
                                                                                                                }}
                                                                                                            >
                                                                                                                <strong>
                                                                                                                    {qIndex + 1}.
                                                                                                                </strong>{" "}
                                                                                                                {question}
                                                                                                            </div>
                                                                                                        ),
                                                                                                    )}

                                                                                                    {round.additional_information && (
                                                                                                        <div
                                                                                                            style={{
                                                                                                                marginTop: "12px",
                                                                                                                background: "#eef4ff",
                                                                                                                borderLeft:
                                                                                                                    "4px solid #1b49ea",
                                                                                                                borderRadius: "6px",
                                                                                                                padding: "10px",
                                                                                                            }}
                                                                                                        >
                                                                                                            <div
                                                                                                                style={{
                                                                                                                    fontSize: "11px",
                                                                                                                    fontWeight: 700,
                                                                                                                    color: "#1b49ea",
                                                                                                                    marginBottom: "4px",
                                                                                                                    textTransform:
                                                                                                                        "uppercase",
                                                                                                                }}
                                                                                                            >
                                                                                                                Additional Information
                                                                                                            </div>

                                                                                                            <div
                                                                                                                style={{
                                                                                                                    fontSize: "12px",
                                                                                                                    color: "#374151",
                                                                                                                    lineHeight: "1.5",
                                                                                                                }}
                                                                                                            >
                                                                                                                {
                                                                                                                    round.additional_information
                                                                                                                }
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            ),
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            ),
                                                                            disabled: true,
                                                                        },
                                                                    ],
                                                                }}
                                                                trigger={["click"]}
                                                                placement="bottomRight"
                                                            >
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    onClick={(e) => e.preventDefault()}
                                                                    style={{
                                                                        fontSize: "11px",
                                                                        color: "#1b49ea",
                                                                        padding: "2px 4px",
                                                                        height: "24px",
                                                                        minWidth: "auto",
                                                                        fontWeight: 500,
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        border: "1px solid #1b49ea",
                                                                        borderRadius: "6px",
                                                                        background: "#fff",
                                                                    }}
                                                                >
                                                                    <span>View Debrief</span>
                                                                    <DownOutlined
                                                                        style={{
                                                                            fontSize: "9px",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            marginLeft: "4px",
                                                                        }}
                                                                    />
                                                                </Button>
                                                            </Dropdown>
                                                        ) : currentRoundNoReason ? (
                                                            // NEW: NO + reason display for this round, tooltip shows the reason on hover
                                                            <Tooltip
                                                                title={
                                                                    currentRoundNoReason.reason ||
                                                                    "No reason provided"
                                                                }
                                                            >
                                                                <span
                                                                    style={{
                                                                        fontSize: "11px",
                                                                        fontWeight: 600,
                                                                        color: "#dc2626",
                                                                        padding: "2px 8px",
                                                                        border: "1px solid #fca5a5",
                                                                        borderRadius: "6px",
                                                                        background: "#fef2f2",
                                                                        cursor: "default",
                                                                    }}
                                                                >
                                                                    NO
                                                                </span>
                                                            </Tooltip>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </td>
                                        <td>
                                            <Styled.IconDiv>
                                                <Tooltip title="View Resume">
                                                    <Styled.ViewButton
                                                        onClick={() => window.open(item.cv_url, "_blank")}
                                                    />
                                                </Tooltip>

                                                {(item.misc_file_url || item.miscellaneous_file) && (
                                                    <Tooltip title="View Miscellaneous File">
                                                        <Styled.MiscFileButton
                                                            onClick={() =>
                                                                window.open(
                                                                    item.misc_file_url || item.miscellaneous_file,
                                                                    "_blank",
                                                                )
                                                            }
                                                        />
                                                    </Tooltip>
                                                )}

                                                {adminAccess === 3 &&
                                                    getIsInterviewConducted(item) &&
                                                    !isUpcomingTable &&
                                                    !item.candidate_status
                                                        ?.toLowerCase()
                                                        .includes("back out") &&
                                                    !item.candidate_status
                                                        ?.toLowerCase()
                                                        .includes("backout") &&
                                                    (!Array.isArray(item.debrief_form) ||
                                                        item.debrief_form.length === 0) &&
                                                    !(
                                                        Array.isArray(item.debrief_no_reason) &&
                                                        item.debrief_no_reason.some((entry) => {
                                                            const currentRound = item.round_name?.match(/\d+/)?.[0];
                                                            const noReasonRound = entry.round_name?.match(/\d+/)?.[0];

                                                            return currentRound && noReasonRound &&
                                                                currentRound === noReasonRound;
                                                        })
                                                    ) ? (
                                                    <Tooltip title="Fill Debriefing Form">
                                                        <button
                                                            style={{
                                                                ...drawerStyles.editIconBtn,
                                                                border: "none",
                                                                color: "#1B49EA",
                                                            }}
                                                            onClick={() =>

                                                                navigate("/debriefing-form", {
                                                                    state: {
                                                                        candidateId: item.candidate_id,
                                                                        candidateName: `${item.first_name || ""} ${item.last_name || ""}`,
                                                                        candidateEmail: item.email,
                                                                        interviewRound: item.round_name,
                                                                        demandId: item.demand_id,
                                                                    },
                                                                })
                                                            }
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.background = "#1B49EA";
                                                                e.currentTarget.style.color = "#ffffff";

                                                                const img =
                                                                    e.currentTarget.querySelector("img");
                                                                if (img) {
                                                                    img.style.filter = "brightness(0) invert(1)";
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.background = "#DEE2EF";
                                                                e.currentTarget.style.color = "#1B49EA";

                                                                const img =
                                                                    e.currentTarget.querySelector("img");
                                                                if (img) {
                                                                    img.style.filter =
                                                                        "invert(27%) sepia(94%) saturate(1600%) hue-rotate(213deg) brightness(95%) contrast(95%)";
                                                                }
                                                            }}
                                                        >
                                                            <img
                                                                src={brief}
                                                                alt="debrief"
                                                                width="24"
                                                                height="24"
                                                                style={{
                                                                    borderRadius: 0,
                                                                    pointerEvents: "none",
                                                                    transition: "filter 0.15s",
                                                                    filter:
                                                                        "invert(27%) sepia(94%) saturate(1600%) hue-rotate(213deg) brightness(95%) contrast(95%)",
                                                                }}
                                                            />
                                                        </button>
                                                    </Tooltip>
                                                ) : (
                                                    ""
                                                )}

                                                {
                                                    adminAccess !== 3 &&
                                                    <Tooltip title="Options">
                                                        <Dropdown overlay={menu(item)} trigger={["click"]}>
                                                            <Styled.OptionButton />
                                                        </Dropdown>
                                                    </Tooltip>
                                                }

                                            </Styled.IconDiv>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Styled.MainTable>
                </Styled.TableWrap>

                {!showEmptyState && !disablePagination && (
                    <div style={paginationStyles.container}>
                        <div style={paginationStyles.leftSection}>
                            <span>Show</span>
                            <Styled.PaginationSelectWrapper>
                                <Select
                                    value={itemsPerPage}
                                    size="small"
                                    dropdownMatchSelectWidth={false}
                                    onChange={(value) => {
                                        setItemsPerPage(value);
                                        setCurrentPage(1);
                                    }}
                                >
                                    <Select.Option value={10}>10</Select.Option>
                                    <Select.Option value={20}>20</Select.Option>
                                    <Select.Option value={50}>50</Select.Option>
                                </Select>
                            </Styled.PaginationSelectWrapper>
                            <span>per page</span>
                            <span style={{ marginLeft: "12px", color: "#9CA3AF" }}>|</span>
                            <span style={{ marginLeft: "12px" }}>
                                {(currentPage - 1) * itemsPerPage + 1}–
                                {Math.min(currentPage * itemsPerPage, listings.length)} of{" "}
                                {listings.length} results
                            </span>
                        </div>

                        <div style={paginationStyles.pageControls}>
                            <button
                                style={paginationStyles.navBtn(currentPage === 1)}
                                onClick={() => setCurrentPage((p) => p - 1)}
                                disabled={currentPage === 1}
                            >
                                ← Prev
                            </button>

                            {buildPageNumbers(currentPage, totalPages).map((page, idx) =>
                                page === "..." ? (
                                    <span
                                        key={`ellipsis-${idx}`}
                                        style={paginationStyles.ellipsis}
                                    >
                                        …
                                    </span>
                                ) : (
                                    <button
                                        key={page}
                                        style={paginationStyles.pageBtn(page === currentPage)}
                                        onClick={() => setCurrentPage(page)}
                                    >
                                        {page}
                                    </button>
                                ),
                            )}

                            <button
                                style={paginationStyles.navBtn(currentPage === totalPages)}
                                onClick={() => setCurrentPage((p) => p + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                )}
            </>
        );
    };

    const renderPreviousInterviewTable = (
        title,
        groupedListings,
        groupsToDisplay,
        currentPage,
        setCurrentPage,
        totalPages,
        itemsPerPage,
        setItemsPerPage,
        disablePagination = false,
    ) => {

        if (!groupedListings?.length) {
            return null;
        }
        const showEmptyState = groupedListings.length === 0;

        const candidateWidth = 200;
        const roundWidth = 90;

        const candidateLeft = 0;
        const roundLeft = candidateLeft + candidateWidth;
        const interviewLeft = roundLeft + roundWidth;

        const groupBorderColor = "#234ca6";
        const totalColumns = 10;

        const renderDebriefCell = (item) => {
            const sortedDebrief = [
                ...(typeof item.debrief_form === "string"
                    ? JSON.parse(item.debrief_form)
                    : item.debrief_form || []),
            ].sort((a, b) => {
                const roundA = parseInt(
                    (a.round_name || "").match(/\d+/)?.[0] || "999",
                );
                const roundB = parseInt(
                    (b.round_name || "").match(/\d+/)?.[0] || "999",
                );
                return roundA - roundB;
            });

            // NEW: Round-specific Debrief display (YES / NO / -)
            // Parse debrief_no_reason the same way debrief_form is parsed above
            const noReasonList = [
                ...(typeof item.debrief_no_reason === "string"
                    ? JSON.parse(item.debrief_no_reason)
                    : item.debrief_no_reason || []),
            ];

            // NEW: extract the numeric round number regardless of "R3" vs
            // "Round 3" formatting - reuses the same digit-extraction
            // approach the existing sort above already relies on, so it
            // follows the round-matching convention already used in this file.
            const getRoundNumber = (roundName) =>
                parseInt((roundName || "").match(/\d+/)?.[0] || "-1", 10);

            const currentRoundNumber = getRoundNumber(item.round_name);

            // NEW: does THIS row's round have a debrief entry?
            const currentRoundDebrief =
                currentRoundNumber !== -1 &&
                sortedDebrief.find(
                    (round) => getRoundNumber(round.round_name) === currentRoundNumber,
                );

            // NEW: does THIS row's round have a NO reason entry?
            const currentRoundNoReason =
                currentRoundNumber !== -1 &&
                noReasonList.find(
                    (round) => getRoundNumber(round.round_name) === currentRoundNumber,
                );

            return (
                <>
                    {currentRoundDebrief ? (
                        // EXISTING: View Debrief dropdown - completely unchanged
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: "content",
                                        label: (
                                            <div
                                                style={{
                                                    maxWidth: "320px",
                                                    padding: "10px",
                                                    position: "relative",
                                                }}
                                            >
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<CopyOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const debriefText = sortedDebrief
                                                            .map(
                                                                (round) =>
                                                                    `${round.round_name}\n${round.questions?.join("\n")}\n${round.additional_information || ""}`,
                                                            )
                                                            .join("\n\n");
                                                        copyDebriefToClipboard(debriefText);
                                                    }}
                                                    style={{
                                                        position: "absolute",
                                                        top: "6px",
                                                        right: "6px",
                                                        padding: 0,
                                                        width: "24px",
                                                        height: "24px",
                                                        minWidth: "24px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        color: "#1b49ea",
                                                    }}
                                                />
                                                <div
                                                    style={{
                                                        fontSize: "12px",
                                                        fontWeight: 600,
                                                        color: "#111827",
                                                        marginBottom: "8px",
                                                        paddingRight: "30px",
                                                    }}
                                                >
                                                    Debrief Details
                                                </div>

                                                <div
                                                    style={{
                                                        maxHeight: "300px",
                                                        overflowY: "auto",
                                                        paddingRight: "4px",
                                                    }}
                                                >
                                                    {sortedDebrief.map((round, index) => (
                                                        <div
                                                            key={index}
                                                            style={{
                                                                marginBottom: "14px",
                                                                background: "#f8fafc",
                                                                border: "1px solid #e5e7eb",
                                                                borderRadius: "10px",
                                                                padding: "12px",
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                    marginBottom: "10px",
                                                                    borderBottom: "1px solid #e5e7eb",
                                                                    paddingBottom: "8px",
                                                                }}
                                                            >
                                                                <div
                                                                    style={{
                                                                        fontWeight: 700,
                                                                        fontSize: "13px",
                                                                        color: "#1b49ea",
                                                                    }}
                                                                >
                                                                    {round.round_name}
                                                                </div>
                                                                <div
                                                                    style={{ fontSize: "11px", color: "#6b7280" }}
                                                                >
                                                                    {round.submitted_at
                                                                        ? new Date(
                                                                            round.submitted_at,
                                                                        ).toLocaleString()
                                                                        : "No Date"}
                                                                </div>
                                                            </div>

                                                            {round.questions?.map((question, qIndex) => (
                                                                <div
                                                                    key={qIndex}
                                                                    style={{
                                                                        padding: "8px 10px",
                                                                        background: "#fff",
                                                                        border: "1px solid #e5e7eb",
                                                                        borderRadius: "6px",
                                                                        marginBottom: "6px",
                                                                        fontSize: "12px",
                                                                        color: "#111827",
                                                                        lineHeight: "1.5",
                                                                    }}
                                                                >
                                                                    <strong>{qIndex + 1}.</strong> {question}
                                                                </div>
                                                            ))}

                                                            {round.additional_information && (
                                                                <div
                                                                    style={{
                                                                        marginTop: "12px",
                                                                        background: "#eef4ff",
                                                                        borderLeft: "4px solid #1b49ea",
                                                                        borderRadius: "6px",
                                                                        padding: "10px",
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            fontSize: "11px",
                                                                            fontWeight: 700,
                                                                            color: "#1b49ea",
                                                                            marginBottom: "4px",
                                                                            textTransform: "uppercase",
                                                                        }}
                                                                    >
                                                                        Additional Information
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            fontSize: "12px",
                                                                            color: "#374151",
                                                                            lineHeight: "1.5",
                                                                        }}
                                                                    >
                                                                        {round.additional_information}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ),
                                        disabled: true,
                                    },
                                ],
                            }}
                            trigger={["click"]}
                            placement="bottomRight"
                        >
                            <Button
                                type="text"
                                size="small"
                                onClick={(e) => e.preventDefault()}
                                style={{
                                    fontSize: "11px",
                                    color: "#1b49ea",
                                    padding: "2px 6px",
                                    height: "24px",
                                    minWidth: "auto",
                                    fontWeight: 600,
                                    display: "flex",
                                    alignItems: "center",
                                    border: "1px solid #c9d8ff",
                                    borderRadius: "6px",
                                    background: "#fff",
                                }}
                            >
                                <span>View Debrief</span>
                                <DownOutlined style={{ fontSize: "9px", marginLeft: "4px" }} />
                            </Button>
                        </Dropdown>
                    ) : currentRoundNoReason ? (
                        // NEW: NO + reason display for this round, tooltip shows the reason on hover
                        <Tooltip title={currentRoundNoReason.reason || "No reason provided"}>
                            <span
                                style={{
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    color: "#dc2626",
                                    padding: "2px 8px",
                                    border: "1px solid #fca5a5",
                                    borderRadius: "6px",
                                    background: "#fef2f2",
                                    cursor: "default",
                                }}
                            >
                                NO
                            </span>
                        </Tooltip>
                    ) : (
                        "-"
                    )}
                </>
            );
        };

        // Reusable detailed row renderer (keeps your existing row layout/logic intact)
        const renderDetailedInterviewRow = (
            item,
            rowKeyPrefix,
            rowBg = "#fff",
            stickyZ = 2,
            options = {},
        ) => {
            const {
                isExpandedChild = false, // true for child rows under an expanded parent
                showCandidateDetails = true, // false => blank the candidate-details cell (child rows)
                extraBorderStyle = {}, // used to draw the grouping box around parent+children
            } = options;

            // status-based tint: child rows always derive their color from the
            // round's own status; single/top-level rows use whatever rowBg was
            // passed in by the caller (which is itself status-derived for singles)
            const statusBg = getRowBackground(item.candidate_status);
            const effectiveBg = isExpandedChild ? statusBg : rowBg;
            const latestReschedule =
                [...(item.interview_schedule_history || [])]
                    .reverse()
                    .find(history => history.action === "Rescheduled");

            return (
                <tr
                    key={rowKeyPrefix}
                    style={{
                        background: effectiveBg,
                        ...extraBorderStyle,
                    }}
                >
                    <td
                        style={{
                            position: "sticky",
                            left: candidateLeft,
                            zIndex: stickyZ,
                            background: effectiveBg,
                            width: "200px",
                            minWidth: "200px",
                            maxWidth: "200px",
                            padding: "8px 12px",
                            boxSizing: "border-box",
                        }}
                    >
                        {showCandidateDetails && (
                            <>
                                {item.first_name} {item.last_name} <br />
                                {item.email} <br />
                                {item.primary_no}
                            </>
                        )}
                    </td>

                    <td
                        style={{
                            position: "sticky",
                            left: roundLeft,
                            zIndex: stickyZ,
                            background: effectiveBg,
                            minWidth: "90px",
                            fontWeight: 600,
                            color: "#1B49EA",
                        }}
                    >
                        {item.round_name || "-"}
                    </td>

                    <td
                        style={{
                            position: "sticky",
                            left: interviewLeft,
                            zIndex: stickyZ,
                            background: effectiveBg,
                            minWidth: "200px",
                        }}
                    >
                        {item.scheduled_datetime
                            ? new Date(item.scheduled_datetime).toDateString() ===
                                todayDateStr
                                ? formatTime(item.scheduled_datetime)
                                : formatDateTime(item.scheduled_datetime)
                            : "-"}
                    </td>

                    <td style={{ background: effectiveBg }}>
                        {item.company_name || "-"}
                    </td>
                    <td style={{ background: effectiveBg }}>{item.role_title || "-"}</td>
                    <td style={{ background: effectiveBg }}>
                        {item.recruiter_name || "-"}
                    </td>
                    <td style={{ background: effectiveBg }}>
                        {item.profile_sent_date ? formatDate(item.profile_sent_date) : "-"}
                    </td>
                    <td style={{ background: effectiveBg }}>
                        <StatusBadge status={item.candidate_status} />
                    </td>
                    <td>
                        {(() => {

                            const latest =
                                item.interview_conducted?.[
                                item.interview_conducted.length - 1
                                ];

                            if (
                                !latest ||
                                latest.interview_conducted === null ||
                                latest.interview_conducted === undefined
                            ) {
                                return "-";
                            }

                            if (latest.interview_conducted === true) {
                                return (
                                    <span
                                        style={{
                                            color: "#16A34A",
                                            fontWeight: 600
                                        }}
                                    >
                                        Yes
                                    </span>
                                );
                            }

                            return (
                                <span
                                    style={{
                                        color: "#DC2626",
                                        fontWeight: 600
                                    }}
                                >
                                    No
                                    {latest.not_conducted_type
                                        ? ` (${latest.not_conducted_type})`
                                        : ""}
                                </span>
                            );

                        })()}
                    </td>
                    <td>
                        {latestReschedule ? (
                            <Tooltip title={latestReschedule.reason ? `Reason: ${latestReschedule.reason}` : "Reason: -"}>
                                <span
                                    style={{
                                        cursor: "pointer",
                                        color: "#1677ff",
                                        fontWeight: 600
                                    }}
                                >
                                    {latestReschedule.reschedule_type || "-"}
                                </span>
                            </Tooltip>
                        ) : (
                            "-"
                        )}
                    </td>
                    <td style={{ background: effectiveBg }}>
                        {(() => {
                            const sortedReschedules =
                                [...(item.interview_schedule_history || [])]
                                    .filter(
                                        (history) =>
                                            history.action === "Rescheduled"
                                    )
                                    .sort(
                                        (a, b) =>
                                            new Date(b.recorded_at) -
                                            new Date(a.recorded_at)
                                    );

                            return sortedReschedules.length > 0 ? (
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: "reschedule-history",
                                                label: (
                                                    <div
                                                        style={{
                                                            width: "400px",
                                                            padding: "4px",
                                                        }}
                                                    >
                                                        {/* Title */}
                                                        <div
                                                            style={{
                                                                fontSize: "14px",
                                                                fontWeight: 700,
                                                                color: "#111827",
                                                                marginBottom: "16px",
                                                                paddingBottom: "10px",
                                                                borderBottom:
                                                                    "1px solid #E5E7EB",
                                                            }}
                                                        >
                                                            Reschedule History
                                                        </div>

                                                        {/* History List */}
                                                        <div
                                                            style={{
                                                                maxHeight: "350px",
                                                                overflowY: "auto",
                                                                paddingRight: "4px",
                                                            }}
                                                        >
                                                            {sortedReschedules.map(
                                                                (history, index) => (
                                                                    <div
                                                                        key={index}
                                                                        style={{
                                                                            padding: "14px",
                                                                            marginBottom:
                                                                                index !==
                                                                                    sortedReschedules.length - 1
                                                                                    ? "10px"
                                                                                    : "0",
                                                                            background:
                                                                                "#FFFFFF",
                                                                            border:
                                                                                "1px solid #E5E7EB",
                                                                            borderRadius:
                                                                                "8px",
                                                                            boxShadow:
                                                                                "0 1px 2px rgba(0, 0, 0, 0.04)",
                                                                        }}
                                                                    >
                                                                        {/* Old Interview Date */}
                                                                        <div
                                                                            style={{
                                                                                marginBottom:
                                                                                    "10px",
                                                                            }}
                                                                        >
                                                                            <div
                                                                                style={{
                                                                                    fontSize:
                                                                                        "10px",
                                                                                    fontWeight:
                                                                                        700,
                                                                                    color:
                                                                                        "#6B7280",
                                                                                    textTransform:
                                                                                        "uppercase",
                                                                                    marginBottom:
                                                                                        "4px",
                                                                                    letterSpacing:
                                                                                        "0.3px",
                                                                                }}
                                                                            >
                                                                                Old Interview Date
                                                                            </div>

                                                                            <div
                                                                                style={{
                                                                                    fontSize:
                                                                                        "14px",
                                                                                    fontWeight:
                                                                                        700,
                                                                                    color:
                                                                                        "#111827",
                                                                                }}
                                                                            >
                                                                                {history.old_scheduled_datetime
                                                                                    ? new Date(
                                                                                        history.old_scheduled_datetime
                                                                                    ).toLocaleString(
                                                                                        "en-GB",
                                                                                        {
                                                                                            day: "2-digit",
                                                                                            month: "long",
                                                                                            year: "numeric",
                                                                                            hour: "2-digit",
                                                                                            minute: "2-digit",
                                                                                        }
                                                                                    )
                                                                                    : "-"}
                                                                            </div>
                                                                        </div>

                                                                        {/* Reschedule Type */}
                                                                        <div
                                                                            style={{
                                                                                marginBottom:
                                                                                    "12px",
                                                                            }}
                                                                        >
                                                                            <span
                                                                                style={{
                                                                                    display:
                                                                                        "inline-block",
                                                                                    padding:
                                                                                        "4px 9px",
                                                                                    borderRadius:
                                                                                        "5px",
                                                                                    fontSize:
                                                                                        "11px",
                                                                                    fontWeight:
                                                                                        600,
                                                                                    color:
                                                                                        history.reschedule_type ===
                                                                                            "Candidate Led"
                                                                                            ? "#2563EB"
                                                                                            : "#7C3AED",
                                                                                    background:
                                                                                        history.reschedule_type ===
                                                                                            "Candidate Led"
                                                                                            ? "#EFF6FF"
                                                                                            : "#F5F3FF",
                                                                                }}
                                                                            >
                                                                                {history.reschedule_type ||
                                                                                    "-"}
                                                                            </span>
                                                                        </div>

                                                                        {/* Reason */}
                                                                        {history.reason && (
                                                                            <div>
                                                                                <div
                                                                                    style={{
                                                                                        fontSize: "10px",
                                                                                        fontWeight: 700,
                                                                                        color: "#6B7280",
                                                                                        textTransform: "uppercase",
                                                                                        marginBottom: "4px",
                                                                                        letterSpacing: "0.3px",
                                                                                    }}
                                                                                >
                                                                                    Reason
                                                                                </div>

                                                                                <div
                                                                                    style={{
                                                                                        fontSize: "12px",
                                                                                        color: "#374151",
                                                                                        lineHeight: "1.5",
                                                                                        wordBreak: "break-word",
                                                                                    }}
                                                                                >
                                                                                    {history.reason}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                ),
                                                disabled: true,
                                            },
                                        ],
                                    }}
                                    trigger={["click"]}
                                    placement="bottomRight"
                                >
                                    <Button
                                        type="text"
                                        size="small"
                                        onClick={(e) =>
                                            e.preventDefault()
                                        }
                                        style={{
                                            fontSize: "11px",
                                            color: "#1B49EA",
                                            padding: "3px 8px",
                                            height: "26px",
                                            fontWeight: 600,
                                            border:
                                                "1px solid #BFDBFE",
                                            borderRadius: "6px",
                                            background: "#EFF6FF",
                                        }}
                                    >
                                        View History

                                        <DownOutlined
                                            style={{
                                                fontSize: "9px",
                                                marginLeft: "5px",
                                            }}
                                        />
                                    </Button>
                                </Dropdown>
                            ) : (
                                "-"
                            );
                        })()}
                    </td>
                    <td>
                        {(() => {
                            const candidateId = item.candidate_id;
                            const cached = debriefHistoryByCandidate[candidateId];

                            // Shared outer "card/popover" wrapper used by every content state.
                            const CONTAINER_STYLE = {
                                width: "400px",
                                boxSizing: "border-box",
                                padding: "16px",
                            };

                            const handleOpenChange = (open) => {
                                if (!open) return;
                                const existing = debriefHistoryByCandidate[candidateId];
                                if (existing && (existing.status === "loading" || existing.status === "success")) {
                                    return; // already fetched/in-flight — avoid duplicate calls
                                }

                                setDebriefHistoryByCandidate((prev) => ({
                                    ...prev,
                                    [candidateId]: { status: "loading", data: [] },
                                }));

                                functionService
                                    .getDebriefHistory({ candidate_id: candidateId })
                                    .then((result) => {
                                        if (!result.status) {
                                            setDebriefHistoryByCandidate((prev) => ({
                                                ...prev,
                                                [candidateId]: { status: "error", data: [] },
                                            }));
                                            return;
                                        }

                                        const payload = result.response?.data;
                                        const rawList = Array.isArray(payload?.data)
                                            ? payload.data
                                            : Array.isArray(payload)
                                                ? payload
                                                : [];

                                        const historyList = rawList.map((h) => ({
                                            ...h,
                                            candidate_name:
                                                h.candidate_name ||
                                                `${h.first_name || ""} ${h.last_name || ""}`.trim() ||
                                                "-",
                                        }));

                                        setDebriefHistoryByCandidate((prev) => ({
                                            ...prev,
                                            [candidateId]: { status: "success", data: historyList },
                                        }));
                                    })
                                    .catch(() => {
                                        setDebriefHistoryByCandidate((prev) => ({
                                            ...prev,
                                            [candidateId]: { status: "error", data: [] },
                                        }));
                                    });
                            };

                            const renderContent = () => {
                                // Not fetched yet — nothing to show before the first click.
                                if (!cached) {
                                    return null;
                                }

                                if (cached.status === "loading") {
                                    return (
                                        <div
                                            style={{
                                                ...CONTAINER_STYLE,
                                                padding: "28px 16px",
                                                textAlign: "center",
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                color: "#6B7280",
                                            }}
                                        >
                                            Loading debrief history...
                                        </div>
                                    );
                                }

                                if (cached.status === "error") {
                                    return (
                                        <div
                                            style={{
                                                ...CONTAINER_STYLE,
                                                padding: "28px 16px",
                                                textAlign: "center",
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                color: "#DC2626",
                                            }}
                                        >
                                            Failed to load debrief history. Please try again.
                                        </div>
                                    );
                                }

                                const historyList = (cached.data || []).filter(
                                    (h) => Array.isArray(h.debrief_form) && h.debrief_form.length > 0
                                );

                                if (historyList.length === 0) {
                                    return renderDebriefHistoryEmpty(CONTAINER_STYLE);
                                }

                                return (
                                    <div style={CONTAINER_STYLE}>
                                        <div
                                            style={{
                                                fontSize: "15px",
                                                fontWeight: 700,
                                                color: "#111827",
                                                marginBottom: "14px",
                                                paddingTop: "2px",
                                                paddingBottom: "12px",
                                                borderBottom: "1px solid #E5E7EB",
                                            }}
                                        >
                                            Debrief History
                                        </div>

                                        <div
                                            style={{
                                                maxHeight: "350px",
                                                overflowY: "auto",
                                                paddingRight: "10px",
                                            }}
                                        >
                                            {historyList.map((candidateHistory, cIndex) => (
                                                <div
                                                    key={candidateHistory.candidate_id || cIndex}
                                                    style={{
                                                        marginBottom:
                                                            cIndex !== historyList.length - 1 ? "14px" : "0",
                                                        padding: "14px",
                                                        background: "#F9FAFB",
                                                        border: "1px solid #E5E7EB",
                                                        borderRadius: "10px",
                                                    }}
                                                >
                                                    {/* Candidate header */}
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "8px",
                                                            marginBottom: "12px",
                                                            paddingBottom: "10px",
                                                            borderBottom: "1px solid #E5E7EB",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                width: "24px",
                                                                height: "24px",
                                                                borderRadius: "50%",
                                                                background: "#DBEAFE",
                                                                color: "#1B49EA",
                                                                fontSize: "11px",
                                                                fontWeight: 700,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            {(candidateHistory.candidate_name || "-")
                                                                .trim()
                                                                .charAt(0)
                                                                .toUpperCase()}
                                                        </div>
                                                        <div style={{ minWidth: 0 }}>
                                                            <div
                                                                style={{
                                                                    fontSize: "14px",
                                                                    fontWeight: 700,
                                                                    color: "#111827",
                                                                    lineHeight: "1.3",
                                                                }}
                                                            >
                                                                {candidateHistory.candidate_name || "-"}
                                                            </div>
                                                            {candidateHistory.email && (
                                                                <div
                                                                    style={{
                                                                        fontSize: "11px",
                                                                        fontWeight: 400,
                                                                        color: "#9CA3AF",
                                                                        marginTop: "1px",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                >
                                                                    {candidateHistory.email}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Rounds — lighter, no competing heavy borders */}
                                                    {candidateHistory.debrief_form.map((round, rIndex) => (
                                                        <div
                                                            key={rIndex}
                                                            style={{
                                                                paddingTop: rIndex !== 0 ? "12px" : "0",
                                                                marginTop: rIndex !== 0 ? "12px" : "0",
                                                                borderTop:
                                                                    rIndex !== 0 ? "1px dashed #E5E7EB" : "none",
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    marginBottom: "8px",
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        display: "inline-block",
                                                                        padding: "3px 10px",
                                                                        borderRadius: "20px",
                                                                        fontSize: "11px",
                                                                        fontWeight: 700,
                                                                        letterSpacing: "0.2px",
                                                                        color: "#1B49EA",
                                                                        background: "#EFF6FF",
                                                                        border: "1px solid #DBEAFE",
                                                                    }}
                                                                >
                                                                    {round.round_name || "-"}
                                                                </span>

                                                                {round.submitted_at && (
                                                                    <span
                                                                        style={{
                                                                            fontSize: "10px",
                                                                            fontWeight: 500,
                                                                            color: "#9CA3AF",
                                                                            textAlign: "right",
                                                                        }}
                                                                    >
                                                                        {new Date(round.submitted_at).toLocaleString(
                                                                            "en-GB",
                                                                            {
                                                                                day: "2-digit",
                                                                                month: "long",
                                                                                year: "numeric",
                                                                                hour: "2-digit",
                                                                                minute: "2-digit",
                                                                            }
                                                                        )}
                                                                    </span>
                                                                )}
                                                            </div>

                                                            {Array.isArray(round.questions) &&
                                                                round.questions.length > 0 && (
                                                                    <div style={{ marginBottom: "10px" }}>
                                                                        <div
                                                                            style={{
                                                                                fontSize: "10px",
                                                                                fontWeight: 700,
                                                                                color: "#2563EB",
                                                                                textTransform: "uppercase",
                                                                                marginBottom: "6px",
                                                                                letterSpacing: "0.4px",
                                                                            }}
                                                                        >
                                                                            Questions
                                                                        </div>
                                                                        <ol
                                                                            style={{
                                                                                margin: 0,
                                                                                paddingLeft: "18px",
                                                                                fontSize: "12px",
                                                                                color: "#374151",
                                                                                lineHeight: "1.7",
                                                                            }}
                                                                        >
                                                                            {round.questions.map((q, qIndex) => (
                                                                                <li
                                                                                    key={qIndex}
                                                                                    style={{ wordBreak: "break-word" }}
                                                                                >
                                                                                    {q}
                                                                                </li>
                                                                            ))}
                                                                        </ol>
                                                                    </div>
                                                                )}

                                                            {round.additional_information && (
                                                                <div>
                                                                    <div
                                                                        style={{
                                                                            fontSize: "10px",
                                                                            fontWeight: 700,
                                                                            color: "#B45309",
                                                                            textTransform: "uppercase",
                                                                            marginBottom: "6px",
                                                                            letterSpacing: "0.4px",
                                                                        }}
                                                                    >
                                                                        Additional Information
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            fontSize: "12px",
                                                                            color: "#374151",
                                                                            lineHeight: "1.6",
                                                                            wordBreak: "break-word",
                                                                        }}
                                                                    >
                                                                        {round.additional_information}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            };

                            return (
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: "debrief-history",
                                                label: renderContent(),
                                                disabled: true,
                                            },
                                        ],
                                    }}
                                    trigger={["click"]}
                                    placement="bottomRight"
                                    onOpenChange={handleOpenChange}
                                >
                                    <Button
                                        type="text"
                                        size="small"
                                        style={{
                                            fontSize: "11px",
                                            color: "#1B49EA",
                                            padding: "3px 8px",
                                            height: "26px",
                                            fontWeight: 600,
                                            border: "1px solid #BFDBFE",
                                            borderRadius: "6px",
                                            background: "#EFF6FF",
                                        }}
                                    >
                                        View History
                                        <DownOutlined style={{ fontSize: "9px", marginLeft: "5px" }} />
                                    </Button>
                                </Dropdown>
                            );
                        })()}
                    </td>
                    <td style={{ background: effectiveBg }}>{renderDebriefCell(item)}</td>
                    <td style={{ background: effectiveBg }}>
                        <Styled.IconDiv>
                            <Tooltip title="View Resume">
                                <Styled.ViewButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(item.cv_url, "_blank");
                                    }}
                                />
                            </Tooltip>

                            {(item.misc_file_url || item.miscellaneous_file) && (
                                <Tooltip title="View Miscellaneous File">
                                    <Styled.MiscFileButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(
                                                item.misc_file_url || item.miscellaneous_file,
                                                "_blank",
                                            );
                                        }}
                                    />
                                </Tooltip>
                            )}

                            {adminAccess === 3 &&
                                getIsInterviewConducted(item) &&
                                !item.candidate_status?.toLowerCase().includes("back out") &&
                                !item.candidate_status?.toLowerCase().includes("backout") &&
                                (!Array.isArray(item.debrief_form) ||
                                    item.debrief_form.length === 0) &&
                                !(
                                    Array.isArray(item.debrief_no_reason) &&
                                    item.debrief_no_reason.some((entry) => {
                                        const currentRound = item.round_name?.match(/\d+/)?.[0];
                                        const noReasonRound = entry.round_name?.match(/\d+/)?.[0];

                                        return (
                                            currentRound &&
                                            noReasonRound &&
                                            currentRound === noReasonRound
                                        );
                                    })
                                ) ? (
                                <Tooltip title="Fill Debriefing Form">
                                    <button
                                        style={{
                                            ...drawerStyles.editIconBtn,
                                            border: "none",
                                            color: "#1B49EA",
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            { console.log("INTERVIEW ITEM:", item) }
                                            navigate("/debriefing-form", {
                                                state: {
                                                    candidateId: item.candidate_id,
                                                    candidateName: `${item.first_name || ""} ${item.last_name || ""}`,
                                                    candidateEmail: item.email,
                                                    interviewRound: item.round_name,
                                                    demandId: item.demand_id,
                                                },
                                            });
                                        }}
                                    >
                                        <img
                                            src={brief}
                                            alt="debrief"
                                            width="24"
                                            height="24"
                                            style={{
                                                borderRadius: 0,
                                                pointerEvents: "none",
                                                transition: "filter 0.15s",
                                                filter:
                                                    "invert(27%) sepia(94%) saturate(1600%) hue-rotate(213deg) brightness(95%) contrast(95%)",
                                            }}
                                        />
                                    </button>
                                </Tooltip>
                            ) : (
                                ""
                            )}

                            {adminAccess !== 3 && (
                                <Tooltip title="Options">
                                    <Dropdown
                                        overlay={menu(item)}
                                        trigger={["click"]}
                                    >
                                        <Styled.OptionButton
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </Dropdown>
                                </Tooltip>
                            )}
                        </Styled.IconDiv>
                    </td>
                </tr>
            );
        };

        return (
            <>
                <h2
                    style={{
                        fontSize: "16px",
                        fontWeight: 700,
                        color: "#1F2937",
                        margin: "10px 0 12px",
                        paddingBottom: "8px",
                        borderBottom: "2px solid #4F46E5",
                        display: "inline-block",
                    }}
                >
                    {title}
                </h2>

                <Styled.TableWrap
                    style={{
                        overflowX: "auto",
                        overflowY: "auto",
                        maxHeight: "calc(100vh - 315px)",
                    }}
                >
                    <Styled.MainTable
                        style={{
                            tableLayout: "fixed",
                            width: "max-content",
                        }}
                    >
                        <thead
                            style={{
                                position: "sticky",
                                top: 0,
                                zIndex: 20,
                                background: "#F2F6F8",
                            }}
                        >
                            <tr>
                                <th
                                    style={{
                                        position: "sticky",
                                        left: candidateLeft,
                                        top: 0,
                                        zIndex: 11,
                                        background: "#F2F6F8",
                                        width: "200px",
                                        minWidth: "200px",
                                        maxWidth: "200px",
                                        padding: "4px 12px",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    Candidate details
                                </th>
                                <th
                                    style={{
                                        position: "sticky",
                                        left: roundLeft,
                                        top: 0,
                                        zIndex: 11,
                                        background: "#F2F6F8",
                                        minWidth: "90px",
                                    }}
                                >
                                    Round
                                </th>
                                <th
                                    style={{
                                        position: "sticky",
                                        left: interviewLeft,
                                        top: 0,
                                        zIndex: 11,
                                        background: "#F2F6F8",
                                        minWidth: "200px",
                                    }}
                                >
                                    Interview Date & Time
                                </th>
                                <th>Account Name</th>
                                <th>Role</th>
                                <th>Recruiter</th>
                                <th>Profile Sent Date</th>
                                <th>Candidate Status</th>
                                <th>Interview Conducted</th>
                                <th>Reschedule Type</th>
                                <th>Reschedule History</th>
                                <th>Debrief History</th>
                                <th>Debrief</th>
                                <th style={{ background: "#F2F6F8" }}>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {groupsToDisplay.map((group, index) => {
                                const candidate = group.latest;
                                const hasMultipleRounds = group.all.length > 1;
                                const isExpanded = !!expandedPreviousRows[group.candidateKey];
                                const sortedRounds = [...group.all].sort(
                                    (a, b) =>
                                        getRoundNumber(b.round_name) - getRoundNumber(a.round_name),
                                );

                                if (!hasMultipleRounds) {
                                    return (
                                        <React.Fragment key={`${group.candidateKey}-${index}`}>
                                            {renderDetailedInterviewRow(
                                                candidate,
                                                `${group.candidateKey}-single`,
                                                getRowBackground(candidate.candidate_status),
                                                2,
                                            )}
                                            {index < groupsToDisplay.length - 1 && (
                                                <tr
                                                    key={`${group.candidateKey}-spacer`}
                                                    aria-hidden="true"
                                                >
                                                    <td
                                                        colSpan={totalColumns}
                                                        style={{
                                                            height: "1px",
                                                            padding: 0,
                                                            border: "none",
                                                            background: "transparent",
                                                        }}
                                                    />
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                }

                                // parent row keeps the current expandable UX coloring only —
                                // no status tint here, per requirement 3 / 4
                                const parentRowBg = getRowBackground(candidate.candidate_status);

                                return (
                                    <React.Fragment key={`${group.candidateKey}-${index}`}>
                                        <tr
                                            onClick={() => togglePreviousRow(group.candidateKey)}
                                            style={{
                                                background: parentRowBg,
                                                cursor: "pointer",
                                                transition: "background-color 0.2s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!isExpanded) {
                                                    const hoverBg = parentRowBg;
                                                    e.currentTarget.style.filter = "brightness(0.94)";
                                                    e.currentTarget
                                                        .querySelectorAll(
                                                            "td, .previous-group-candidate-line",
                                                        )
                                                        .forEach((el) => {
                                                            el.style.background = hoverBg;
                                                        });
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = parentRowBg;
                                                e.currentTarget.style.filter = "none";
                                                e.currentTarget
                                                    .querySelectorAll(
                                                        "td, .previous-group-candidate-line",
                                                    )
                                                    .forEach((el) => {
                                                        el.style.background = parentRowBg;
                                                    });
                                            }}
                                        >
                                            <Styled.PreviousGroupHeaderCell
                                                style={{
                                                    background: parentRowBg,
                                                    borderTop: `2px solid ${groupBorderColor}`,
                                                    borderLeft: `2px solid ${groupBorderColor}`,
                                                    borderBottom: isExpanded
                                                        ? "none"
                                                        : `2px solid ${groupBorderColor}`,
                                                    boxShadow: "2px 0 4px -2px rgba(0, 0, 0, 0.06)",
                                                }}
                                            >
                                                <div
                                                    className="previous-group-candidate-line"
                                                    style={{ background: parentRowBg }}
                                                >
                                                    <Styled.Chevron open={isExpanded}>
                                                    </Styled.Chevron>

                                                    <span
                                                        style={{
                                                            fontWeight: 700,
                                                            color: "#111827",
                                                            fontSize: "14px",
                                                        }}
                                                    >
                                                        {candidate.first_name} {candidate.last_name}
                                                    </span>

                                                    <span style={{ color: "#4B5563", fontSize: "13px", fontWeight: 600 }}>
                                                        {candidate.email}
                                                    </span>

                                                    <span style={{ color: "#4B5563", fontSize: "13px", fontWeight: 600 }}>
                                                        {candidate.primary_no}
                                                    </span>
                                                </div>
                                            </Styled.PreviousGroupHeaderCell>

                                            {/* ADD THIS */}
                                            {Array.from({ length: 13 }).map((_, idx) => (
                                                <td
                                                    key={idx}
                                                    style={{
                                                        background: parentRowBg,
                                                        borderTop: `2px solid ${groupBorderColor}`,
                                                        borderBottom: isExpanded
                                                            ? "none"
                                                            : `2px solid ${groupBorderColor}`,
                                                    }}
                                                />
                                            ))}
                                        </tr>

                                        {isExpanded &&
                                            sortedRounds.map((round, idx) => {
                                                const isFirst = idx === 0;
                                                const isLast = idx === sortedRounds.length - 1;
                                                return renderDetailedInterviewRow(
                                                    round,
                                                    `${group.candidateKey}-round-${idx}`,
                                                    previousRowUXStyles.childRow,
                                                    2,
                                                    {
                                                        isExpandedChild: true,
                                                        showCandidateDetails: false,
                                                        extraBorderStyle: {
                                                            borderLeft: `2px solid ${groupBorderColor}`,
                                                            borderRight: `2px solid ${groupBorderColor}`,
                                                            borderTop: isFirst
                                                                ? `1px solid ${groupBorderColor}`
                                                                : "none",
                                                            borderBottom: isLast
                                                                ? `2px solid ${groupBorderColor}`
                                                                : `1px solid #E0EAFF`,
                                                            boxShadow: isLast
                                                                ? "0 4px 6px -2px rgba(27, 73, 234, 0.08)"
                                                                : "none",
                                                        },
                                                    },
                                                );
                                            })}

                                        {index < groupsToDisplay.length - 1 && (
                                            <tr
                                                key={`${group.candidateKey}-spacer`}
                                                aria-hidden="true"
                                            >
                                                <td
                                                    colSpan={totalColumns}
                                                    style={{
                                                        height: "1px",
                                                        padding: 0,
                                                        border: "none",
                                                        background: "transparent",
                                                    }}
                                                />
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </Styled.MainTable>
                </Styled.TableWrap>

                {!showEmptyState && !disablePagination && (
                    <div style={paginationStyles.container}>
                        <div style={paginationStyles.leftSection}>
                            <span>Show</span>
                            <Styled.PaginationSelectWrapper>
                                <Select
                                    value={itemsPerPage}
                                    size="small"
                                    dropdownMatchSelectWidth={false}
                                    onChange={(value) => {
                                        setItemsPerPage(value);
                                        setCurrentPage(1);
                                    }}
                                >
                                    <Select.Option value={10}>10</Select.Option>
                                    <Select.Option value={20}>20</Select.Option>
                                    <Select.Option value={50}>50</Select.Option>
                                </Select>
                            </Styled.PaginationSelectWrapper>
                            <span>per page</span>
                            <span style={{ marginLeft: "12px", color: "#9CA3AF" }}>|</span>
                            <span style={{ marginLeft: "12px" }}>
                                {(currentPage - 1) * itemsPerPage + 1}–
                                {Math.min(currentPage * itemsPerPage, groupedListings.length)}{" "}
                                of {groupedListings.length} results
                            </span>
                        </div>

                        <div style={paginationStyles.pageControls}>
                            <button
                                style={paginationStyles.navBtn(currentPage === 1)}
                                onClick={() => setCurrentPage((p) => p - 1)}
                                disabled={currentPage === 1}
                            >
                                ← Prev
                            </button>

                            {buildPageNumbers(currentPage, totalPages).map((page, idx) =>
                                page === "..." ? (
                                    <span
                                        key={`ellipsis-${idx}`}
                                        style={paginationStyles.ellipsis}
                                    >
                                        …
                                    </span>
                                ) : (
                                    <button
                                        key={page}
                                        style={paginationStyles.pageBtn(page === currentPage)}
                                        onClick={() => setCurrentPage(page)}
                                    >
                                        {page}
                                    </button>
                                ),
                            )}

                            <button
                                style={paginationStyles.navBtn(currentPage === totalPages)}
                                onClick={() => setCurrentPage((p) => p + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                )}
            </>
        );
    };
    const hasTodayData = todayInterviewListings.length > 0;

    const hasUpcomingData =
        !selectedDebriefStatus &&
        allInterviewListings.length > 0;

    const hasPreviousData =
        groupedPreviousInterviewListings.length > 0;

    const shouldShowEmptyState = showInterviewHistory
        ? !hasPreviousData
        : !hasTodayData && !hasUpcomingData;

    return (
        <Styled.MainDiv>
            <Sidemenu />

            <Styled.ContentWrapDiv>
                <Styled.TopContainer>
                    <div className="leftSide">Interview Tracking</div>

                    <div
                        className="rightSide"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "16px",
                        }}
                    >
                        {/* Interview History Checkbox - Visible for everyone */}
                        <label
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                cursor: "pointer",
                                padding: "8px 12px",
                                background: showInterviewHistory ? "#E8F0FF" : "#F9FAFB",
                                borderRadius: "8px",
                                border: "1px solid #E5E7EB",
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={showInterviewHistory}
                                onChange={(e) => setShowInterviewHistory(e.target.checked)}
                                style={{
                                    width: "18px",
                                    height: "18px",
                                    cursor: "pointer",
                                    accentColor: "#1B49EA",
                                }}
                            />
                            <span
                                style={{
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    color: showInterviewHistory ? "#1B49EA" : "#374151",
                                }}
                            >
                                Interview History
                            </span>
                        </label>

                        {/* Export only for non-adminAccess 3 */}
                        {adminAccess != 3 && <button onClick={exportToCSV}>Export</button>}
                    </div>
                </Styled.TopContainer>

                <div
                    style={{
                        display: "flex",
                        gap: "12px",
                        marginBottom: "20px",
                        overflowX: "auto",
                        flexWrap: "nowrap",
                        paddingBottom: "6px",
                        scrollbarWidth: "thin",
                        scrollbarColor: "#D1D5DB transparent",
                    }}
                >
                    <Select
                        showSearch
                        placeholder="Select an account"
                        onChange={setSelectedCompany}
                        allowClear
                        style={{
                            minWidth: "180px",
                            flexShrink: 0,
                        }}
                        value={selectedCompany}
                    >
                        {[
                            ...new Set(
                                filteredJobListings
                                    .map((item) => item.company_name)
                                    .filter(Boolean)
                            ),
                        ].map((company) => (
                            <Select.Option
                                key={company}
                                value={company}
                            >
                                {company}
                            </Select.Option>
                        ))}
                    </Select>

                    <Select
                        showSearch
                        placeholder="Select a role"
                        onChange={setSelectedRole}
                        allowClear
                        style={{ minWidth: "180px", flexShrink: 0 }}
                        value={selectedRole}
                    >
                        {roleOptions.map((role) => (
                            <Select.Option key={role} value={role}>
                                {role}
                            </Select.Option>
                        ))}
                    </Select>

                    {adminAccess != 3 && (
                        <Select
                            showSearch
                            placeholder="Sub-account"
                            onChange={setSelectedSubAccount}
                            allowClear
                            style={{ minWidth: "180px", flexShrink: 0 }}
                            value={selectedSubAccount}
                        >
                            {subAccountOptions.map((s) => (
                                <Select.Option key={s} value={s}>
                                    {s}
                                </Select.Option>
                            ))}
                        </Select>
                    )}

                    {adminAccess != 3 && (
                        <Select
                            showSearch
                            placeholder="End client"
                            onChange={setSelectedEndClient}
                            allowClear
                            style={{ minWidth: "180px", flexShrink: 0 }}
                            value={selectedEndClient}
                        >
                            {endClientOptions.map((e) => (
                                <Select.Option key={e} value={e}>
                                    {e}
                                </Select.Option>
                            ))}
                        </Select>
                    )}
                    <RangePicker
                        value={interviewDateRange}
                        onChange={(dates) => setInterviewDateRange(dates || null)}
                        format="DD MMM YYYY"
                        placeholder={["Interview From", "Interview To"]}
                        separator={
                            <span
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "24px",
                                    minWidth: "24px",
                                    flexShrink: 0,
                                    color: "#6B7280",
                                    fontWeight: 600,
                                    fontSize: "16px",
                                    lineHeight: "1",
                                }}
                            >
                                →
                            </span>
                        }
                        allowClear
                        style={{
                            width: "262px",
                            minWidth: "262px",
                            flexShrink: 0,
                        }}
                    />

                    {adminAccess != 3 && (
                        <Select
                            showSearch
                            placeholder="Select a recruiter"
                            onChange={setSelectedRecruiter}
                            allowClear
                            style={{ minWidth: "180px", flexShrink: 0 }}
                            value={selectedRecruiter}
                        >
                            {recruiterOptions.map((item) => (
                                <Select.Option
                                    key={item.recruiter_name}
                                    value={item.recruiter_name}
                                >
                                    {item.recruiter_name}
                                </Select.Option>
                            ))}
                        </Select>
                    )}

                    <Input
                        placeholder="Search by Name / Email"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        allowClear
                        style={{ maxWidth: "200px", flexShrink: 0 }}
                    />

                    <Select
                        placeholder="Candidate Status"
                        value={selectedCandidateStatus}
                        onChange={setSelectedCandidateStatus}
                        allowClear
                        style={{ minWidth: "220px", flexShrink: 0 }}
                    >
                        {candidateStatusOptions.map((status) => (
                            <Select.Option key={status} value={status}>
                                {status}
                            </Select.Option>
                        ))}
                    </Select>

                    <Select
                        placeholder="Round"
                        value={selectedRound}
                        onChange={setSelectedRound}
                        allowClear
                        style={{ minWidth: "120px", flexShrink: 0 }}
                    >
                        <Select.Option value="R1">R1</Select.Option>
                        <Select.Option value="R2">R2</Select.Option>
                        <Select.Option value="R3">R3</Select.Option>
                        <Select.Option value="R4">R4</Select.Option>
                    </Select>

                    <Select
                        placeholder="Debrief History"
                        value={selectedDebriefStatus}
                        onChange={setSelectedDebriefStatus}
                        allowClear
                        style={{ minWidth: "160px", flexShrink: 0 }}
                    >
                        <Select.Option value="Filled">Filled</Select.Option>
                        <Select.Option value="Not Filled">Not Filled</Select.Option>
                    </Select>

                    <DatePicker
                        placeholder="Debrief Date"
                        value={selectedDebriefDate}
                        onChange={(date) => setSelectedDebriefDate(date || null)}
                        format="DD MMM YYYY"
                        disabledDate={disableDebriefDates}
                        allowClear
                        style={{ width: "200px", flexShrink: 0 }}
                    />

                </div>


                {/* {renderTableSection(
                    "Today's Interviews",
                    todayInterviewListings,
                    todayItemsToDisplay,
                    todayCurrentPage,
                    setTodayCurrentPage,
                    totalTodayPages,
                    todayItemsPerPage,
                    setTodayItemsPerPage,
                    true
                )}

                {renderTableSection(
                    "All Interviews",
                    allInterviewListings,
                    allItemsToDisplay,
                    allCurrentPage,
                    setAllCurrentPage,
                    totalAllPages,
                    allItemsPerPage,
                    setAllItemsPerPage,
                    false
                )} */}

                {showInterviewHistory ? (
                    renderPreviousInterviewTable(
                        "Previous Interviews",
                        groupedPreviousInterviewListings,
                        previousItemsToDisplay,
                        previousCurrentPage,
                        setPreviousCurrentPage,
                        totalPreviousPages,
                        previousItemsPerPage,
                        setPreviousItemsPerPage,
                        false,
                    )
                ) : (
                    <>
                        {todayInterviewListings.length > 0 &&
                            renderTableSection(
                                "Today's Interviews",
                                todayInterviewListings,
                                todayItemsToDisplay,
                                todayCurrentPage,
                                setTodayCurrentPage,
                                totalTodayPages,
                                todayItemsPerPage,
                                setTodayItemsPerPage,
                                true,
                            )}

                        {!selectedDebriefStatus &&
                            allInterviewListings.length > 0 &&
                            renderTableSection(
                                "Upcoming Interviews",
                                allInterviewListings,
                                allItemsToDisplay,
                                allCurrentPage,
                                setAllCurrentPage,
                                totalAllPages,
                                allItemsPerPage,
                                setAllItemsPerPage,
                                false,
                                true // isUpcomingTable
                            )}
                    </>
                )}

                {shouldShowEmptyState && (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "400px",
                            textAlign: "center",
                        }}
                    >
                        <img
                            src={require("../../images/Frame.png")}
                            alt="No Data"
                            style={{
                                marginTop: "55px",
                                width: "90%",
                                maxWidth: "400px",
                                height: "auto",
                            }}
                        />

                        <h2
                            style={{
                                marginTop: "16px",
                                color: "#6B7280",
                                fontWeight: 500,
                            }}
                        >
                            No Result Found
                        </h2>
                    </div>
                )}

                {/* ADD MODAL HERE */}
                <Modal
                    open={
                        isInterviewNotConductedModalVisible
                    }
                    title="Interview Not Conducted"
                    onCancel={() => {
                        setIsInterviewNotConductedModalVisible(
                            false
                        );

                        setNotConductedType(null);
                    }}
                    footer={[
                        <Button
                            key="cancel"
                            onClick={() => {
                                setIsInterviewNotConductedModalVisible(
                                    false
                                );

                                setNotConductedType(null);
                            }}
                        >
                            Cancel
                        </Button>,

                        <Button
                            key="submit"
                            type="primary"
                            onClick={
                                submitInterviewNotConducted
                            }
                        >
                            Submit
                        </Button>
                    ]}
                >
                    <div
                        style={{
                            marginBottom: "16px"
                        }}
                    >
                        <strong>Candidate:</strong>{" "}
                        {
                            selectedInterviewConducted?.first_name
                        }{" "}
                        {
                            selectedInterviewConducted?.last_name
                        }
                    </div>

                    <Select
                        placeholder="Select reason type"
                        value={notConductedType}
                        onChange={
                            setNotConductedType
                        }
                        style={{ width: "100%" }}
                    >
                        <Select.Option value="Candidate Led">
                            Candidate Led
                        </Select.Option>

                        <Select.Option value="Client Led">
                            Client Led
                        </Select.Option>
                    </Select>
                </Modal>
                <Drawer
                    title="Update Profile Status"
                    placement="right"
                    width={420}
                    open={isProfileDrawerVisible}
                    onClose={closeProfileStatusDrawer}
                >
                    {/* Candidate Information */}
                    {/* <div
                        style={{
                            marginBottom: "24px",
                            padding: "16px",
                            background: "#F8FAFC",
                            border: "1px solid #E5E7EB",
                            borderRadius: "10px",
                        }}
                    >
                        <div style={{ marginBottom: "8px" }}>
                            <strong>Candidate:</strong>{" "}
                            {selectedProfileCandidate?.first_name}{" "}
                            {selectedProfileCandidate?.last_name}
                        </div>

                        <div style={{ marginBottom: "8px" }}>
                            <strong>Email:</strong>{" "}
                            {selectedProfileCandidate?.email}
                        </div>

                        <div style={{ marginBottom: "8px" }}>
                            <strong>Round:</strong>{" "}
                            {selectedProfileCandidate?.round_name}
                        </div>
                    </div> */}

                    {/* Candidate Status */}
                    <div style={{ marginBottom: "24px" }}>
                        <label
                            style={{
                                display: "block",
                                fontWeight: 600,
                                fontSize: "14px",
                                marginBottom: "8px",
                            }}
                        >
                            Candidate Current Status
                        </label>

                        <Select
                            placeholder="Select status"
                            value={updateCandidateStatus}
                            onChange={(val) => {
                                setUpdateCandidateStatus(val);

                                if (val) {
                                    setUpdateCandidateStatusChangedDate(
                                        resolveStatusChangedDate(
                                            val,
                                            selectedProfileCandidate?.status_history
                                        )
                                    );
                                } else {
                                    setUpdateCandidateStatusChangedDate(null);
                                }

                                if (!interviewStatuses.includes(val)) {
                                    setInterviewDateTime(null);
                                }
                            }}
                            allowClear
                            showSearch
                            style={{ width: "100%" }}
                        >
                            {candidateStatusOptions.map((status) => {
                                const isGlobalAvailable =
                                    statusFlow.ALWAYS_AVAILABLE.includes(status);

                                const isDisabled =
                                    !allowedStatuses.includes(status) ||
                                    (
                                        !isCurrentStatusGlobal &&
                                        !isGlobalAvailable &&
                                        fullCandidateStatusOptions.indexOf(status) < currentStatusIndex
                                    );

                                return (
                                    <Select.Option
                                        key={status}
                                        value={status}
                                        disabled={isDisabled}
                                    >
                                        {status}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </div>

                    {/* Interview Date */}
                    {showInterviewDateTime && (
                        <div style={{ marginBottom: "24px" }}>
                            <label
                                style={{
                                    display: "block",
                                    fontWeight: 600,
                                    fontSize: "14px",
                                    marginBottom: "8px",
                                }}
                            >
                                Interview Date & Time
                            </label>

                            <DatePicker
                                showTime={{
                                    format: "HH:mm",
                                    showSecond: false,
                                    minuteStep: 5,
                                }}
                                format="DD-MM-YYYY HH:mm"
                                popupStyle={{
                                    width: "420px",
                                    right: "10px",
                                }}
                                disabledDate={(current) =>
                                    current && current < dayjs().startOf("day")
                                }
                                style={{
                                    width: "100%",
                                    height: "40px",
                                }}
                                value={interviewDateTime}
                                onChange={(date) =>
                                    setInterviewDateTime(date)
                                }
                            />
                        </div>
                    )}

                    {/* Status Changed Date */}
                    <div style={{ marginBottom: "24px" }}>
                        <label
                            style={{
                                display: "block",
                                fontWeight: 600,
                                fontSize: "14px",
                                marginBottom: "8px",
                            }}
                        >
                            Status Changed Date
                        </label>

                        <DatePicker
                            placeholder="Select date"
                            value={updateCandidateStatusChangedDate}
                            disabledDate={(current) =>
                                current && current < dayjs().startOf("day")
                            }
                            onChange={(date) => {
                                setUpdateCandidateStatusChangedDate(
                                    date || null
                                );

                                if (!date) {
                                    setUpdateCandidateStatus(null);
                                }
                            }}
                            format="DD MMM YYYY"
                            allowClear
                            style={{ width: "100%" }}
                        />

                        {updateCandidateStatus && (
                            <div
                                style={{
                                    fontSize: "12px",
                                    color: "#6B7280",
                                    marginTop: "4px",
                                }}
                            >
                                Uses status history date when
                                available, otherwise today's date.
                            </div>
                        )}
                    </div>

                    {/* Client Feedback */}
                    <div
                        style={{
                            borderTop: "1px solid #E5E7EB",
                            paddingTop: "20px",
                            marginBottom: "24px",
                        }}
                    >
                        <label
                            style={{
                                display: "block",
                                fontWeight: 600,
                                fontSize: "14px",
                                marginBottom: "8px",
                            }}
                        >
                            Client Feedback
                        </label>

                        <Input.TextArea
                            rows={4}
                            placeholder="Enter client's feedback"
                            value={client_feedback}
                            onChange={(e) =>
                                setClientFeedback(
                                    e.target.value
                                )
                            }
                        />
                    </div>

                    {/* Client Remark */}
                    <div
                        style={{
                            marginBottom: "24px",
                        }}
                    >
                        <label
                            style={{
                                display: "block",
                                fontWeight: 600,
                                fontSize: "14px",
                                marginBottom: "8px",
                            }}
                        >
                            Client Remark
                        </label>

                        <Input.TextArea
                            rows={4}
                            placeholder="Enter client's remark"
                            value={updateClientRemark}
                            onChange={(e) =>
                                setUpdateClientRemark(
                                    e.target.value
                                )
                            }
                        />
                    </div>

                    <div
                        style={{
                            padding: "16px",
                            background: "#F9FAFB",
                            borderRadius: "10px",
                            border: "1px solid #E5E7EB",
                            marginBottom: "24px",
                        }}
                    >
                        <div
                            style={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#9CA3AF",
                                marginBottom: "12px",
                                textTransform: "uppercase",
                            }}
                        >
                            Candidate Details
                        </div>

                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px",
                                fontSize: "13px",
                                marginBottom: "20px",
                            }}
                        >
                            <div>
                                <strong>Candidate:</strong>{" "}
                                {selectedProfileCandidate?.first_name}{" "}
                                {selectedProfileCandidate?.last_name}
                            </div>

                            <div>
                                <strong>Email:</strong>{" "}
                                {selectedProfileCandidate?.email || "-"}
                            </div>

                            <div>
                                <strong>Round:</strong>{" "}
                                {selectedProfileCandidate?.round_name || "-"}
                            </div>
                        </div>

                        <div
                            style={{
                                borderTop: "1px solid #E5E7EB",
                                paddingTop: "16px",
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#9CA3AF",
                                marginBottom: "12px",
                                textTransform: "uppercase",
                            }}
                        >
                            Current Values
                        </div>

                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "8px",
                                fontSize: "13px",
                            }}
                        >
                            <div>
                                <strong>Status:</strong>{" "}
                                {selectedProfileCandidate?.candidate_status || "-"}
                            </div>

                            <div>
                                <strong>Interview Date:</strong>{" "}
                                {selectedProfileCandidate?.scheduled_datetime
                                    ? dayjs(
                                        selectedProfileCandidate.scheduled_datetime
                                    ).format("DD MMM YYYY HH:mm")
                                    : "-"}
                            </div>

                            <div>
                                <strong>Feedback:</strong>{" "}
                                {selectedProfileCandidate?.client_feedback || "-"}
                            </div>

                            <div>
                                <strong>Remark:</strong>{" "}
                                {selectedProfileCandidate?.remarks || "-"}
                            </div>
                        </div>
                    </div>

                    {/* Footer Buttons */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "12px",
                            marginTop: "30px",
                        }}
                    >
                        <Button
                            onClick={
                                closeProfileStatusDrawer
                            }
                        >
                            Cancel
                        </Button>

                        <Button
                            type="primary"
                            onClick={
                                handleUpdateProfileStatus
                            }
                        >
                            Save Changes
                        </Button>
                    </div>
                </Drawer>
            </Styled.ContentWrapDiv>
            <Drawer
                title="Reschedule Interview"
                placement="right"
                width={420}
                open={isInterviewDrawerVisible}
                onClose={closeInterviewDrawer}
            >
                {/* <div style={{ marginBottom: "20px" }}>
                    <div>
                        <strong>Candidate:</strong>{" "}
                        {selectedInterview?.first_name} {selectedInterview?.last_name}
                    </div>

                    <div>
                        <strong>Email:</strong>{" "}
                        {selectedInterview?.email}
                    </div>

                    <div>
                        <strong>Round:</strong>{" "}
                        {selectedInterview?.round_name}
                    </div>
                </div> */}

                <div style={{ marginBottom: "20px" }}>
                    <label>Reschedule Type</label>

                    <Select
                        value={rescheduleType}
                        onChange={setRescheduleType}
                        style={{
                            width: "100%",
                            marginTop: "8px",
                        }}
                    >
                        <Select.Option value="Candidate Led">
                            Candidate Led
                        </Select.Option>

                        <Select.Option value="Client Led">
                            Client Led
                        </Select.Option>
                    </Select>
                </div>

                <div style={{ marginBottom: "20px" }}>
                    <label>Current Interview Time</label>

                    <DatePicker
                        showTime
                        disabled
                        value={
                            selectedInterview?.scheduled_datetime
                                ? dayjs(selectedInterview.scheduled_datetime)
                                : null
                        }
                        style={{ width: "100%", marginTop: "8px" }}
                    />
                </div>

                <div style={{ marginBottom: "20px" }}>
                    <label>
                        New Interview Time <span style={{ color: "red" }}>*</span>
                    </label>

                    <DatePicker
                        showTime
                        value={newInterviewDate}
                        onChange={(value) => {
                            setNewInterviewDate(value);

                            if (value) {
                                setNewInterviewDateError("");
                            }
                        }}
                        disabledDate={(current) =>
                            current && current < dayjs().startOf("day")
                        }
                        status={newInterviewDateError ? "error" : ""}
                        style={{
                            width: "100%",
                            marginTop: "8px"
                        }}
                        placeholder="Select new interview date and time"
                    />

                    {newInterviewDateError && (
                        <div
                            style={{
                                color: "#ff4d4f",
                                fontSize: "12px",
                                marginTop: "4px"
                            }}
                        >
                            {newInterviewDateError}
                        </div>
                    )}
                </div>

                <div style={{ marginBottom: "20px" }}>
                    <label>Reason</label>

                    <Input.TextArea
                        rows={4}
                        value={reason}
                        onChange={(e) =>
                            setReason(e.target.value)
                        }
                        placeholder="Enter reason for rescheduling"
                        style={{
                            marginTop: "8px"
                        }}
                    />
                </div>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "10px",
                        marginTop: "30px",
                    }}
                >
                    <Button onClick={closeInterviewDrawer}>
                        Cancel
                    </Button>

                    <Button
                        type="primary"
                        onClick={handleRescheduleInterview}
                    >
                        Save Changes
                    </Button>
                </div>
            </Drawer>

            <Drawer
                title="Edit Interview Date & Time"
                placement="right"
                width={420}
                open={isEditInterviewDateTimeVisible}
                onClose={closeEditInterviewDateTime}
            >
                {/* Candidate */}
                <div style={{ marginBottom: "20px" }}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: 500,
                        }}
                    >
                        Candidate
                    </label>

                    <Input
                        value={`${selectedEditInterview?.first_name || ""} ${
                            selectedEditInterview?.last_name || ""
                        }`.trim()}
                        disabled
                    />
                </div>

                {/* Round */}
                <div style={{ marginBottom: "20px" }}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: 500,
                        }}
                    >
                        Round
                    </label>

                    <Input
                        value={selectedEditInterview?.round_name || ""}
                        disabled
                    />
                </div>

                {/* Current Interview Date & Time */}
                <div style={{ marginBottom: "20px" }}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: 500,
                        }}
                    >
                        Current Interview Date & Time
                    </label>

                    <DatePicker
                        showTime
                        disabled
                        style={{ width: "100%" }}
                        value={
                            selectedEditInterview?.scheduled_datetime
                                ? dayjs(
                                    selectedEditInterview.scheduled_datetime
                                )
                                : null
                        }
                    />
                </div>

                {/* New Interview Date & Time */}
                <div style={{ marginBottom: "20px" }}>
                    <label
                        style={{
                            display: "block",
                            marginBottom: "8px",
                            fontWeight: 500,
                        }}
                    >
                        New Interview Date & Time{" "}
                        <span style={{ color: "red" }}>*</span>
                    </label>

                    <DatePicker
                        showTime
                        style={{ width: "100%" }}
                        value={editInterviewDateTime}
                        format="DD MMM YYYY, hh:mm A"
                        onChange={(value) => {
                            setEditInterviewDateTime(value);
                            setEditInterviewDateTimeError("");
                        }}
                        disabledDate={(current) =>
                            current &&
                            current < dayjs().startOf("day")
                        }
                        status={
                            editInterviewDateTimeError
                                ? "error"
                                : ""
                        }
                    />

                    {editInterviewDateTimeError && (
                        <div
                            style={{
                                color: "#ff4d4f",
                                fontSize: "12px",
                                marginTop: "5px",
                            }}
                        >
                            {editInterviewDateTimeError}
                        </div>
                    )}
                </div>

                {/* Buttons */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "10px",
                        marginTop: "30px",
                    }}
                >
                    <Button
                        onClick={closeEditInterviewDateTime}
                    >
                        Cancel
                    </Button>

                    <Button
                        type="primary"
                        onClick={handleEditInterviewDateTime}
                    >
                        Save Changes
                    </Button>
                </div>
            </Drawer>
        </Styled.MainDiv>

    );
};


export default InterviewTable;